import { useState, useEffect, useRef, useCallback, useMemo, Component } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Slider } from "@/components/ui/slider";
import PhygitalIcon, { PHYGITAL_AVATAR_BLACK } from "@/components/PhygitalIcon";
import AdminSwitchboard from "@/components/AdminSwitchboard";
import { useAdminCopy } from "@/contexts/AdminCopyContext";
import { DefaultAvatar, DefaultOrgIcon, useHostingCoverConfig } from "@/components/DefaultAvatars";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import BottomNav from "@/components/BottomNav";
import { useFlag } from "@/pages/phygital/hooks/usePhygitalFlags";
import AdminBar from "@/pages/phygital/AdminBar";
import ExplainerWidget, { InlineTip, TIPS } from "@/components/ExplainerWidget";
import { InstagramEmbedCard } from "@/components/InstagramEmbed";
import { getCategoryPreset } from "@/lib/categoryConfig";
import LogoutButton from "@/components/LogoutButton";
import { LinkedAccountsSection } from "@/components/profile/LinkedAccountsSection";
import { WalletCardSettings, UpcomingEventsSection, HostingSection, ProfileCompletionCard } from "@/components/profile/DashboardComponents";
import PendingHostApplicationsCard from "@/components/profile/PendingHostApplicationsCard";
import { DefaultsTab } from "@/components/profile/DefaultsTab";
import UniversalAllergensBlock from "@/components/UniversalAllergensBlock";
import JoinedBanner from "@/components/JoinedBanner";
import HostDashboardSwitcher from "@/components/HostDashboardSwitcher";
import InspoFoldersGrid from "@/components/inspo/InspoFoldersGrid";
import HostNoteModal from "@/components/host/HostNoteModal";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { AboutEditModal, AboutInfoCard, ROLE_OPTIONS } from "@/components/profile/AboutSection";
import { SettingsTab } from "@/components/profile/SettingsTab";
import SharePreferenceCardModal, { PreferenceCardPreview } from "@/components/profile/SharePreferenceCardModal";
import ExperiencesPage from "@/pages/ExperiencesPage";
import { PreferenceCardModal, PreferenceCardTrigger } from "@/components/profile/PreferenceCardModal";
import { PlanExperienceModal, PlanEventModal } from "@/components/experiences/PlanModals";
import { PLAN_CTAS } from "@/lib/planCtaCopy";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";
import ViewAsBanner from "@/components/ViewAsBanner";
import NotificationCenter from "@/components/NotificationCenter";
import NotificationBell from "@/components/notifications/NotificationBell";
// Removed: SyncBadge / triggerSyncStart / triggerSyncEnd (sync UI deprecated)
import { AttendeePreferenceCard } from "@/components/AttendeePreferenceCard";
import UnifiedPreferenceCard from "@/components/UnifiedPreferenceCard";
import UnifiedTopBar from "@/components/UnifiedTopBar";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuth } from "@/App";
import { isSuperAdminEmail } from "@/config/platform";
import { toast } from "sonner";
import Cropper from "react-easy-crop";
import { useAllUserData, revalidateAllUserData, revalidateEventData, useSyncListener, triggerGlobalSync } from "@/hooks/useUserData";
import {
  ArrowUpFromLine,
  Calendar as CalendarIcon,
  Scissors,
  Sparkles,
  Palette,
  Eye,
  Settings,
  Check,
  Plus,
  LayoutDashboard,
  LogOut,
  Wand2,
  Camera,
  Trash2,
  Send,
  Share,
  ChevronRight,
  Heart,
  Edit3,
  Utensils,
  Plane,
  ShoppingBag,
  Upload,
  Link as LinkIcon,
  Instagram,
  Folder,
  Image as ImageIcon,
  BookOpen,
  User,
  Phone,
  Mail,
  Globe,
  ChevronDown,
  CalendarDays,
  Clock,
  PartyPopper,
  X,
  Copy,
  Cake,
  Shield,
  Key,
  AlertTriangle,
  Download,
  QrCode,
  Printer,
  Search,
  Bell,
  Users,
  Home,
  CreditCard,
  SlidersHorizontal,
  Building,
  MessageCircle,
  RefreshCw,
  Lock,
  Accessibility,
  CheckCircle2,
  Circle,
  Maximize2,
  ZoomIn,
  RotateCw,
  Smartphone,
  Link2,
  Binoculars,
  ExternalLink,
  UserPlus,
  Clock3,
  Headphones,
  Dog,
  Wind,
  HandHeart,
  Activity,
  Zap,
  Coffee,
  Brain,
  TrendingUp,
  Star
} from "lucide-react";

// Design System Colors (Phygital Brand)
const THEME = {
  primary: "#000000",
  primaryLight: "#71717A",
  secondary: "#F4F4F5",
  background: "#FAFAFA",
  surface: "#FFFFFF",
  text: "#1A1A1A",
  muted: "#71717A",
};

// Accessibility Options - matches RSVP intake exactly
const ACCESSIBILITY_OPTIONS = [
  { id: "wheelchair", label: "Wheelchair accessible", desc: "Need wheelchair-accessible space", icon: Accessibility, color: "bg-blue-100 text-blue-600" },
  { id: "extra_time", label: "Extra time needed", desc: "May need more time between activities", icon: Clock3, color: "bg-purple-100 text-purple-600" },
  { id: "hearing", label: "Hearing assistance", desc: "May need visual cues or written communication", icon: Headphones, color: "bg-teal-100 text-teal-600" },
  { id: "service_animal", label: "Service animal", desc: "Will have a service animal present", icon: Dog, color: "bg-amber-100 text-amber-600" },
  { id: "fragrance_free", label: "Fragrance-free", desc: "Sensitive to scents and fragrances", icon: Wind, color: "bg-green-100 text-green-600" },
  { id: "limited_mobility", label: "Limited mobility", desc: "May need assistance with positioning", icon: HandHeart, color: "bg-rose-100 text-rose-600" }
];

// Invisible Disabilities - matches RSVP intake exactly
const INVISIBLE_DISABILITIES = [
  { id: "vocal_tics", label: "Vocal tics", desc: "I may make sudden sounds or verbal expressions - please continue normally", icon: Activity, color: "bg-violet-100 text-violet-600" },
  { id: "motor_tics", label: "Motor tics", desc: "I may have sudden movements or gestures - please continue normally", icon: Zap, color: "bg-orange-100 text-orange-600" },
  { id: "tourettes", label: "Tourette's Syndrome", desc: "I have a combination of vocal and motor tics - please continue normally", icon: Activity, color: "bg-pink-100 text-pink-600" },
  { id: "anxiety", label: "Anxiety", desc: "I may need extra patience, breaks, or reassurance", icon: Heart, color: "bg-red-100 text-red-600" },
  { id: "chronic_pain", label: "Chronic pain", desc: "I may need to adjust my position frequently", icon: AlertTriangle, color: "bg-amber-100 text-amber-600" },
  { id: "chronic_fatigue", label: "Chronic fatigue", desc: "I may need rest breaks during the event", icon: Coffee, color: "bg-cyan-100 text-cyan-600" },
  { id: "adhd", label: "ADHD", desc: "I may need reminders or have difficulty with long sessions", icon: Brain, color: "bg-indigo-100 text-indigo-600" },
  { id: "autism", label: "Autism spectrum", desc: "I may have specific sensory or communication preferences", icon: Sparkles, color: "bg-emerald-100 text-emerald-600" },
  { id: "epilepsy", label: "Epilepsy", desc: "Please be aware of potential seizures - I will share emergency info", icon: Zap, color: "bg-yellow-100 text-yellow-600" }
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Error Boundary for crash-prone tabs
class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <AlertTriangle className="w-8 h-8 text-amber-500 mb-3" />
          <p className="text-sm font-medium text-gray-700 mb-1">Something went wrong loading this tab</p>
          <p className="text-xs text-gray-400 mb-3 text-center max-w-xs">{this.state.error?.message}</p>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper to get image URL - handles relative paths, absolute URLs, etc.
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('data:')) return path;
  // Path includes /api/ prefix, use base URL
  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/api$/, '') || '';
  if (path.startsWith('/api/')) return `${baseUrl}${path}`;
  return `${baseUrl}/api${path.startsWith('/') ? '' : '/'}${path}`;
};

// Helper to get displayable image URL (handles external URLs via proxy)
const getDisplayImageUrl = (url, inspoItem = null) => {
  // Handle Instagram embeds - return placeholder since they don't have direct image URLs
  if (inspoItem?.embed_type === 'instagram' || inspoItem?.source === 'instagram_embed') {
    // Instagram embeds don't have extractable image URLs, return a placeholder
    return null;
  }
  if (!url) return null;
  // If it's already a local upload or proxy URL, return as-is
  if (url.startsWith('/api/') || url.startsWith('data:')) return url;
  // If it's a full URL with API prefix, return as-is
  if (url.startsWith('http') && url.includes('/api/')) return url;
  // For external URLs that aren't direct images (social media), use proxy
  const socialDomains = ['instagram.com', 'tiktok.com', 'pinterest.com', 'twitter.com', 'x.com'];
  if (socialDomains.some(domain => url.toLowerCase().includes(domain))) {
    return `${API}/proxy/image?url=${encodeURIComponent(url)}`;
  }
  // For other URLs (like direct image URLs), return as-is
  return url;
};

// Get the best cover image from experience inspirations (excludes Instagram embeds)
const getCoverImage = (exp) => {
  if (!exp?.inspiration_images?.length) return null;
  // Find the first inspiration with a valid, non-Instagram image
  for (const img of exp.inspiration_images) {
    if (img.embed_type === 'instagram' || img.source === 'instagram_embed') continue;
    const displayUrl = getDisplayImageUrl(img.image_url, img);
    if (displayUrl) return displayUrl;
  }
  return null;
};

// Custom nail polish icon - SVG vector style matching other category icons
const NailPolishIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Nail polish bottle */}
    <path d="M12 2v3" />
    <rect x="9" y="5" width="6" height="4" rx="1" />
    {/* Bottle body - almond shaped */}
    <path d="M8 9h8l1 3v6c0 2-1.5 4-5 4s-5-2-5-4v-6l1-3z" />
    {/* Polish inside */}
    <path d="M9 14c0 1.5 1.5 3 3 3s3-1.5 3-3" />
  </svg>
);

// Custom lash icon - SVG vector style matching other category icons  
const LashIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Lash lines - curved swooshes */}
    <path d="M3 12c2-3 5-5 9-5s7 2 9 5" />
    <path d="M5 10c1.5-2 3.5-3.5 7-3.5s5.5 1.5 7 3.5" />
    <path d="M7 8c1.5-1.5 3-2.5 5-2.5s3.5 1 5 2.5" />
    {/* Eye line */}
    <path d="M4 14c3 2 6 3 8 3s5-1 8-3" strokeWidth="2" />
  </svg>
);

const CATEGORIES = {
  hair: { icon: Scissors, color: "bg-[#ffeef4] text-[#e83e8c]", gradient: "from-[#e83e8c] to-pink-500", lightGradient: "from-[#ffeef4] to-pink-50", name: "Hair" },
  nails: { icon: NailPolishIcon, color: "bg-[#ffe8ec] text-[#f15a70]", gradient: "from-[#f15a70] to-red-400", lightGradient: "from-[#ffe8ec] to-red-50", name: "Nails" },
  face: { icon: Palette, color: "bg-[#f2e9ff] text-[#9c57f5]", gradient: "from-[#9c57f5] to-purple-500", lightGradient: "from-[#f2e9ff] to-purple-50", name: "Face" },
  lash: { icon: LashIcon, color: "bg-[#ede7f7] text-[#774cda]", gradient: "from-[#774cda] to-violet-500", lightGradient: "from-[#ede7f7] to-violet-50", name: "Lashes" },
  accessibility: { icon: Accessibility, color: "bg-teal-100 text-teal-600", gradient: "from-teal-500 to-cyan-500", lightGradient: "from-teal-50 to-cyan-50", name: "Accessibility" },
  dietary: { icon: Utensils, color: "bg-green-100 text-green-600", gradient: "from-green-500 to-emerald-500", lightGradient: "from-green-50 to-emerald-50", name: "Dietary" },
  interests: { icon: Heart, color: "bg-rose-100 text-rose-600", gradient: "from-rose-500 to-pink-500", lightGradient: "from-rose-50 to-pink-50", name: "Interests" },
  audience: { icon: Users, color: "bg-[#e0eeff] text-[#4a90d9]", gradient: "from-[#4a90d9] to-blue-500", lightGradient: "from-[#e0eeff] to-blue-50", name: "Audience" },
  style: { icon: ShoppingBag, color: "bg-[#f0e8ff] text-[#9950fa]", gradient: "from-[#9950fa] to-purple-500", lightGradient: "from-[#f0e8ff] to-purple-50", name: "Style" },
  culinary: { icon: Utensils, color: "bg-[#fff3e6] text-[#f28c28]", gradient: "from-[#f28c28] to-orange-500", lightGradient: "from-[#fff3e6] to-orange-50", name: "Food" },
  wellness: { icon: Heart, color: "bg-[#e7f5f5] text-[#75d6d6]", gradient: "from-[#75d6d6] to-teal-400", lightGradient: "from-[#e7f5f5] to-teal-50", name: "Wellness" },
  travel: { icon: Plane, color: "bg-[#e7f3ff] text-[#73b7ff]", gradient: "from-[#73b7ff] to-blue-400", lightGradient: "from-[#e7f3ff] to-blue-50", name: "Travel" },
  events: { icon: CalendarIcon, color: "bg-[#fff5e6] text-[#f9c434]", gradient: "from-[#f9c434] to-yellow-500", lightGradient: "from-[#fff5e6] to-yellow-50", name: "Events" }
};

// Phygital Brand Colors from Canva
const BRAND_COLORS = [
  { id: "black", hex: "#000000", name: "Black" },
  { id: "blue", hex: "#2563eb", name: "Blue" },
  { id: "pink", hex: "#db2777", name: "Pink" },
  { id: "orange", hex: "#f67315", name: "Orange" },
  { id: "purple", hex: "#9332ea", name: "Purple" },
  { id: "green", hex: "#27803d", name: "Green" },
  { id: "teal", hex: "#2e9488", name: "Teal" },
];

// Categories for defaults tab - NOW DYNAMIC from API
// This will be populated from the platform_defaults endpoint
const PILOT_CATEGORIES = ['dietary', 'accessibility', 'interests', 'audience'];

// Categories for inspo tab - Should be based on user's pilot access
// These are the VISUAL INSPO categories (images/looks) - NOT defaults
// Updated per FINAL_PLATFORM_ARCHITECTURE.md:
// - Beauty: Hair, Nails, Face, Lash
// - Lifestyle: Style, Travel
// NOTE: These should eventually come from admin config per user's pilot access
const INSPO_CATEGORIES = ['hair', 'nails', 'face', 'lash', 'style', 'culinary'];

// Legacy - kept for backward compatibility but should migrate to INSPO_CATEGORIES
const BEAUTY_CATEGORIES = INSPO_CATEGORIES;

// Categories for experiences tab 
const EXPERIENCE_CATEGORIES = ['hair', 'nails', 'face', 'lash', 'style', 'culinary'];
// Event-related categories (auto-sync from event RSVPs)
const EVENT_DEFAULT_CATEGORIES = ['accessibility', 'dietary', 'interests'];

// Occasion options for experiences
const OCCASION_OPTIONS = [
  { id: "just_planning", label: "Just Planning", iconName: "Sparkles" },
  { id: "maintenance", label: "Regular Maintenance", iconName: "RefreshCw" },
  { id: "special_event", label: "Special Event", iconName: "PartyPopper" },
  { id: "wedding", label: "Wedding/Bridal", iconName: "Heart" },
  { id: "birthday", label: "Birthday", iconName: "Cake" },
  { id: "date_night", label: "Date Night", iconName: "HeartHandshake" },
  { id: "vacation", label: "Vacation", iconName: "Plane" },
  { id: "holiday", label: "Holiday", iconName: "Snowflake" },
  { id: "new_look", label: "New Look", iconName: "Star" },
];

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-700" },
  ready: { label: "Ready", color: "bg-blue-100 text-blue-600" },
  sent: { label: "Sent", color: "bg-amber-100 text-amber-600" },
  pending: { label: "Pending", color: "bg-orange-100 text-orange-600" },
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-600" },
  booked: { label: "Booked", color: "bg-green-100 text-green-600" },
  completed: { label: "Completed", color: "bg-purple-100 text-purple-600" }
};


// Quick Access Portals - loads user's assigned portals dynamically
const QuickAccessPortals = ({ user, navigate, API }) => {
  const t = useAdminCopy();
  const [portals, setPortals] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
        const res = await fetch(`${API}/user/portals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPortals(data.portals || []);
        }
      } catch {}
      setLoaded(true);
    };
    load();
  }, [API]);

  const PORTAL_CONFIG = {
    investor: { icon: TrendingUp, color: "text-emerald-600", desc: "Updates, pitch deck & projections" },
    team: { icon: Users, color: "text-blue-600", desc: "Team updates & Phygital HQ" },
    network: { icon: Globe, color: "text-purple-600", desc: "Network updates & connections" },
  };

  const getPortalRoute = (p) => {
    if (p.type === "investor") return `/investor/portal/${p.portal_id}`;
    if (p.type === "team") return `/team/portal/${p.portal_id}`;
    if (p.type === "network") return `/network/portal/${p.portal_id}`;
    return "#";
  };

  return (
    <div className="space-y-2">
      {/* Admin Control (password-protected) + Switchboard */}
      {user?.email === "me@iamnaomithomas.com" && (
        <>
          <div 
            className="w-full p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => navigate("/admin/control")}
            data-testid="settings-admin-link"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-gray-600" />
              <div className="flex-1">
                <span className="font-medium text-sm">{t("admin.control.link_title", "Admin Control")}</span>
                <p className="text-xs text-gray-500">{t("admin.control.link_subtitle", "Password-protected super-panel")}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          </div>
          <AdminSwitchboard />
        </>
      )}

      {/* Dynamic portals */}
      {portals.map((portal) => {
        const config = PORTAL_CONFIG[portal.type] || PORTAL_CONFIG.network;
        const Icon = config.icon;
        const portalName = portal.name ? `${portal.type === "investor" ? "Investor Portal" : portal.type === "team" ? "Team Portal" : "Network Portal"}: ${portal.name}` : `${portal.type === "investor" ? "Investor Portal" : portal.type === "team" ? "Team Portal" : "Network Portal"}`;
        return (
          <div 
            key={portal.portal_id}
            className="w-full p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => navigate(getPortalRoute(portal))}
            data-testid={`settings-${portal.type}-portal-link`}
          >
            <div className="flex items-center gap-3">
              <Icon className={`w-5 h-5 ${config.color}`} />
              <div className="flex-1">
                <span className="font-medium text-sm">{portalName}</span>
                <p className="text-xs text-gray-500">{config.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        );
      })}

      {/* Advocate link for users with advocate role */}
      {user?.roles?.includes("advocate") && (
        <div 
          className="w-full p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => navigate("/advocate")}
          data-testid="settings-advocate-link"
        >
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <span className="font-medium text-sm">Advocate Portal</span>
              <p className="text-xs text-gray-500">Track referrals & earnings</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>
      )}
    </div>
  );
};

// Super Admin Pilots Section - Shows all pilot host dashboards organized by category
const SuperAdminPilotsSection = ({ navigate }) => {
  const [dashboards, setDashboards] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API_BASE}/api/admin/all-host-dashboards`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          setDashboards(data.dashboards || []);
          setByCategory(data.by_category || {});
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return null;
  if (dashboards.length === 0) return null;

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  // Iter 9.30: Split Pilot Dashboards (live testers) vs Demo Dashboards (canonical templates).
  // The /me/admin view now mirrors the Switchboard split that already lives on the Settings tab.
  const pilotItems = dashboards.filter(d => d.is_pilot && !d.is_demo);
  const demoItems = dashboards.filter(d => d.is_demo);
  const groupByCat = (list) => {
    // Iter 9.38: nest instructor add-on dashboards under their parent provider.
    // Detection priority: linked_instructor_dashboard_id (forward), parent_event_id,
    // linked_provider_event_id, dashboard_kind=instructor, is_instructor=true.
    const byId = {};
    list.forEach(d => { byId[d.event_id] = { ...d, _addons: [] }; });
    // (1) Forward link: parent.linked_instructor_dashboard_id → child
    list.forEach(d => {
      if (d.linked_instructor_dashboard_id && byId[d.linked_instructor_dashboard_id]) {
        byId[d.event_id]._addons.push(byId[d.linked_instructor_dashboard_id]);
        byId[d.linked_instructor_dashboard_id]._is_addon = true;
      }
    });
    // (2) Reverse link: child has parent_event_id or linked_provider_event_id
    list.forEach(d => {
      const parentId = d.parent_event_id || d.linked_provider_event_id;
      if (parentId && byId[parentId] && !byId[d.event_id]._is_addon) {
        // attach if not already
        if (byId[parentId]._addons.findIndex(a => a.event_id === d.event_id) === -1) {
          byId[parentId]._addons.push(byId[d.event_id]);
        }
        byId[d.event_id]._is_addon = true;
      }
    });
    // (3) Heuristic: same host has a dashboard with is_instructor=true OR dashboard_kind=instructor
    //     pair it with the sibling provider dashboard
    const byHost = {};
    list.forEach(d => {
      if (!byId[d.event_id]._is_addon) {
        (byHost[d.host_user_id] = byHost[d.host_user_id] || []).push(d.event_id);
      }
    });
    Object.values(byHost).forEach(ids => {
      if (ids.length < 2) return;
      const provider = ids.find(id => {
        const x = byId[id];
        return !x.is_instructor && x.dashboard_kind !== "instructor" && (x.category || "").toLowerCase() !== "instructor";
      });
      const instructor = ids.find(id => {
        const x = byId[id];
        return x.is_instructor || x.dashboard_kind === "instructor" || (x.category || "").toLowerCase() === "instructor";
      });
      if (provider && instructor && provider !== instructor && byId[provider]._addons.findIndex(a => a.event_id === instructor) === -1) {
        byId[provider]._addons.push(byId[instructor]);
        byId[instructor]._is_addon = true;
      }
    });
    // Now bucket non-addon dashboards by category
    const acc = {};
    list.forEach(d => {
      const enriched = byId[d.event_id];
      if (enriched._is_addon) return; // skip - rendered under parent
      const k = (enriched.pilot_category || enriched.category || "other").toLowerCase();
      (acc[k] = acc[k] || []).push(enriched);
    });
    return acc;
  };
  const pilotByCat = groupByCat(pilotItems);
  const demoByCat = groupByCat(demoItems);

  const renderRow = (d, opts = {}) => {
    const isAddon = !!opts.addon;
    return (
      <div key={d.event_id}>
        <div
          className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${isAddon ? "pl-10 bg-gray-50/50 border-l-2 border-violet-200" : ""}`}
          onClick={() => navigate(`/host/v2/${d.event_id}`)}
          data-testid={`pilot-dashboard-${d.event_id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {isAddon && <span className="text-violet-500 text-xs">↳</span>}
              <p className="text-sm font-medium truncate">{d.name}</p>
              {isAddon && <span className="text-[9px] uppercase tracking-wider text-violet-600 font-semibold">Add-on</span>}
            </div>
            <p className="text-xs text-gray-500 truncate">{d.host_name} {d.host_company ? `- ${d.host_company}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            {d.is_demo && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">DEMO</span>}
            {d.is_pilot && <span className="text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">PILOT</span>}
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${d.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{d.status}</span>
          </div>
        </div>
        {(d._addons || []).map(addon => renderRow(addon, { addon: true }))}
      </div>
    );
  };

  const renderGroup = (label, prefix, byCat, count, color) => {
    if (count === 0) return null;
    return (
      <div className="mb-4" data-testid={`super-admin-${prefix}-section`}>
        <div className="flex items-center gap-2 mb-2 px-1">
          <h4 className={`text-[11px] font-semibold uppercase tracking-wider ${color}`}>{label}</h4>
          <span className="text-[10px] text-gray-400">({count})</span>
        </div>
        <div className="space-y-2">
          {Object.entries(byCat).map(([cat, items]) => {
            // Iter 9.38 - accurate count: top-level hosts + count add-ons separately
            const addonCount = items.reduce((acc, d) => acc + (d._addons?.length || 0), 0);
            const countLabel = addonCount > 0
              ? `${items.length} host${items.length !== 1 ? 's' : ''} · ${addonCount} add-on${addonCount !== 1 ? 's' : ''}`
              : `${items.length}`;
            return (
              <div key={cat} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                <button
                  onClick={() => toggleCat(`${prefix}-${cat}`)}
                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                  data-testid={`pilot-cat-toggle-${prefix}-${cat}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium capitalize">{cat}</span>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" data-testid={`pilot-cat-count-${prefix}-${cat}`}>{countLabel}</span>
                  </div>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedCats[`${prefix}-${cat}`] ? "rotate-90" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
                {expandedCats[`${prefix}-${cat}`] && (
                  <div className="border-t divide-y">{items.map(renderRow)}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="mx-4 mt-4" data-testid="super-admin-pilots-section">
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="w-6 h-6 rounded-md bg-orange-100 flex items-center justify-center">
          <svg className="w-3.5 h-3.5 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /></svg>
        </div>
        <h3 className="text-sm font-semibold text-gray-800">Host Dashboards</h3>
        {(() => {
          // Iter 9.38 - count UNIQUE HOSTS, not raw dashboard rows. Add-ons listed separately.
          const isAddon = d => d.is_instructor || d.dashboard_kind === "instructor" || (d.category || "").toLowerCase() === "instructor" || d.parent_event_id || d.linked_provider_event_id;
          const allTops = dashboards.filter(d => !isAddon(d));
          const allAddons = dashboards.filter(isAddon);
          const pilotTops = allTops.filter(d => d.is_pilot && !d.is_demo);
          const demoTops = allTops.filter(d => d.is_demo);
          return (
            <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full" data-testid="host-dashboards-count">
              {allTops.length} host{allTops.length !== 1 ? 's' : ''} · {allAddons.length} add-on{allAddons.length !== 1 ? 's' : ''} · {pilotTops.length} pilot · {demoTops.length} demo
            </span>
          );
        })()}
      </div>
      {renderGroup("Pilots", "pilot", pilotByCat, pilotItems.length, "text-emerald-700")}
      {renderGroup("Demos", "demo", demoByCat, demoItems.length, "text-purple-700")}
    </div>
  );
};

// /me Pilots section - shows consumer pilot users who have /me access
const MePilotsSection = ({ navigate }) => {
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [impersonating, setImpersonating] = useState(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API_BASE}/api/admin/me-pilots`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          setPilots(data.pilots || []);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  const viewAsPilot = async (userId) => {
    setImpersonating(userId);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API_BASE}/api/admin/me-pilots/${userId}/impersonate`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" }
      });
      if (res.ok) {
        const data = await res.json();
        window.open(`${window.location.origin}/me?imp_token=${data.session_token}`, '_blank');
      } else {
        toast.error("Failed to impersonate pilot");
      }
    } catch { toast.error("Failed to impersonate"); }
    setImpersonating(null);
  };

  if (loading || pilots.length === 0) return null;

  return (
    <div className="mx-4 mt-4" data-testid="me-pilots-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-2 px-1"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-violet-100 flex items-center justify-center">
            <Users className="w-3.5 h-3.5 text-violet-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-800">/me Pilots</h3>
          <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{pilots.length}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white divide-y">
          {pilots.map(p => (
            <div
              key={p.user_id}
              className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 cursor-pointer"
              onClick={() => viewAsPilot(p.user_id)}
              data-testid={`me-pilot-${p.user_id}`}
            >
              {p.profile_image ? (
                <img src={p.profile_image} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 text-xs font-bold">
                  {(p.name || p.email || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{p.name || p.email}</p>
                <p className="text-[10px] text-gray-500 truncate">{p.email}</p>
              </div>
              <div className="flex items-center gap-1.5">
                {impersonating === p.user_id && <div className="w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />}
                {p.is_demo && <span className="text-[8px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">DEMO</span>}
                {p.categories?.map(cat => (
                  <span key={cat} className="text-[8px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full capitalize">{cat}</span>
                ))}
                <ExternalLink className="w-3 h-3 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const UserProfilePage = ({ defaultTab }) => {
  // Phygital Beauty · gate hosting UI behind admin flag so bare-bones planner
  // stays clean until Booking phase is ready. Admins see it via AdminBar override.
  const hostingSectionVisible = useFlag("host_dashboard_section");
  // Iter 9.40 Pass G - read ?tab=… from URL so deep-links land on the right tab.
  const initialTab = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("tab");
      if (t) return t;
    } catch { /* SSR-safe */ }
    return defaultTab || "home";
  })();
  const navigate = useNavigate();
  const { logout, refreshUser } = useAuth();
  const { settings: companySettings } = useCompanySettings();
  // Iter 9.47.j - `/me` page copy comes from Airtable `surface_me` slots.
  const { slotMap: meSlots } = useUiSurface("surface_me");
  const planCtaService = {
    ...PLAN_CTAS.service,
    title:    slotText(meSlots, "plan_service_title",    PLAN_CTAS.service.title),
    subtitle: slotText(meSlots, "plan_service_subtitle", PLAN_CTAS.service.subtitle),
  };
  const planCtaOccasion = {
    ...PLAN_CTAS.occasion,
    title:    slotText(meSlots, "plan_occasion_title",    PLAN_CTAS.occasion.title),
    subtitle: slotText(meSlots, "plan_occasion_subtitle", PLAN_CTAS.occasion.subtitle),
  };
  const upcomingSectionTitle = slotText(meSlots, "upcoming_section_title", "Upcoming");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("experiences");
  const [mobileTab, setMobileTab] = useState(initialTab);
  const [preferences, setPreferences] = useState({});
  // Note: experiences, inspirations, hostedEvents, attendingEvents are now from SWR directly

  // Iter 9.36 - show waitlist banner if user just joined the waitlist
  const [waitlistBanner, setWaitlistBanner] = useState(() => {
    if (typeof window === "undefined") return null;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("waitlist") === "joined") {
      return { category: sp.get("category") || "" };
    }
    return null;
  });

  // Pass 2 - Draft host dashboard banner
  // If user landed here because their dashboard is in admin-draft mode,
  // surface a status banner. Cleared on dismiss or once dashboard is active.
  const [draftBanner, setDraftBanner] = useState(() => {
    if (typeof window === "undefined") return null;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("draft") === "true") {
      try {
        const raw = localStorage.getItem("draft_dashboard_notice");
        return raw ? JSON.parse(raw) : { event_id: null, name: "your dashboard" };
      } catch (_e) {
        return { event_id: null, name: "your dashboard" };
      }
    }
    return null;
  });
  
  // Handle user updates - updates local state AND triggers global sync
  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    // Dispatch global sync event so other components (like Host Dashboard) get the update
    window.dispatchEvent(new CustomEvent('phygital:sync', { detail: { type: 'user' } }));
    // Also dispatch user-updated for auth context
    window.dispatchEvent(new CustomEvent('phygital:user-updated', { detail: updatedUser }));
    // Also refresh auth context
    refreshUser();
  };
  
  // Listen for global sync events from other pages
  useSyncListener();
  const [copied, setCopied] = useState(false);
  
  // Card settings (toggles)
  const [cardSettings, setCardSettings] = useState({
    show_photo: true,
    show_full_name: true,
    nickname: "",
    show_social_media: false,
    show_title: false,
    show_accessibility: false,
    accessibility_notes: "",
    social_handles: {}
  });
  const [showCardSettings, setShowCardSettings] = useState(false);
  
  // Note: hostedEvents and attendingEvents now come directly from SWR
  
  // Preview modal for events/experiences
  const [previewEvent, setPreviewEvent] = useState(null);

  // Iter 9.47.q - Universal confirm-delete state for experiences
  const [deletingExperience, setDeletingExperience] = useState(null);
  const [deletingExpInFlight, setDeletingExpInFlight] = useState(false);
  
  // Wallet card color customization
  const [walletColor, setWalletColor] = useState(() => {
    return localStorage.getItem("phygital_wallet_color") || "#000000"; // Black default
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // Visible wallet categories - users can hide categories they don't use
  const [visibleCategories, setVisibleCategories] = useState(() => {
    const saved = localStorage.getItem("phygital_visible_categories");
    if (saved) return JSON.parse(saved);
    // New users start with empty - they'll add what they need
    return [];
  });
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  
  const toggleCategory = (catId) => {
    const newVisible = visibleCategories.includes(catId)
      ? visibleCategories.filter(c => c !== catId)
      : [...visibleCategories, catId];
    setVisibleCategories(newVisible);
    localStorage.setItem("phygital_visible_categories", JSON.stringify(newVisible));
  };
  
  const changeWalletColor = (hex) => {
    setWalletColor(hex);
    localStorage.setItem("phygital_wallet_color", hex);
    setShowColorPicker(false);
  };
  
  // Calendar state
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  
  // User defaults (synced from event preferences)
  const [userDefaults, setUserDefaults] = useState(null);
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  
  // Investor portal (if user has one)
  const [investorPortal, setInvestorPortal] = useState(null);
  
  // Create experience modal - now step-based
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState(1); // 1: Category & Occasion, 2: Defaults & Inspo, 3: Details
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newExperience, setNewExperience] = useState({ name: "", category: "nails", target_date: "", stylist_note: "", inspiration_ids: [], custom_adjustments: {}, no_date_yet: false, occasion: "" });
  const [selectedInspos, setSelectedInspos] = useState([]);
  const [creatingExperience, setCreatingExperience] = useState(false);
  
  // Edit experience modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);
  
  // Edit profile modal
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [editProfileData, setEditProfileData] = useState({ name: "", first_name: "", last_name: "", email: "", phone: "" });
  
  // Link provider modal
  const [showLinkProviderModal, setShowLinkProviderModal] = useState(false);
  const [linkingExperience, setLinkingExperience] = useState(null);
  const [providerInfo, setProviderInfo] = useState({ name: "", phone: "", email: "", website: "", instagram: "" });
  
  // Instagram linking & verification
  const [instagramUsername, setInstagramUsername] = useState("");
  const [savingInstagram, setSavingInstagram] = useState(false);
  const [verificationStep, setVerificationStep] = useState(null); // null | 'code'
  const [verificationCode, setVerificationCode] = useState("");
  const [pendingVerificationCode, setPendingVerificationCode] = useState("");
  
  // Wallet preview modal
  const [showWalletPreview, setShowWalletPreview] = useState(false);
  
  // Share wallet modal with experience selection
  const [showShareWalletModal, setShowShareWalletModal] = useState(false);
  const [selectedShareExperience, setSelectedShareExperience] = useState(null);
  
  // New share preference card modal (with visual preview)
  const [showSharePreferenceCard, setShowSharePreferenceCard] = useState(false);
  // Plan modals open as overlays on the dashboard (R: stay on dashboard, do not navigate to /experiences tab)
  const [showPlanService, setShowPlanService] = useState(false);
  const [showPlanOccasion, setShowPlanOccasion] = useState(false);
  // Iter 9.51 - Deep-link support. `?plan_service=1` opens step 1 (category picker),
  // `?plan_service=makeup|hair|nails|style|lashes|routine` opens step 2 pre-filled.
  // `?plan_occasion=1` opens the occasion picker.
  const [planServicePrefillCategory, setPlanServicePrefillCategory] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const ps = params.get("plan_service");
    if (ps) {
      const validCats = ["makeup", "hair", "nails", "style", "lashes", "routine"];
      setPlanServicePrefillCategory(validCats.includes(ps) ? ps : null);
      setShowPlanService(true);
    }
    if (params.get("plan_occasion")) {
      setShowPlanOccasion(true);
    }
  }, []);
  
  // Preference Card Modal (triggered by eyeball icon)
  const [showPreferenceCardModal, setShowPreferenceCardModal] = useState(false);
  
  // Provider search modal
  const [showProviderSearchModal, setShowProviderSearchModal] = useState(false);
  const [providerSearchQuery, setProviderSearchQuery] = useState("");
  const [providerSearchResults, setProviderSearchResults] = useState([]);
  const [searchingProviders, setSearchingProviders] = useState(false);
  // Note: notifications and unreadNotifications come from SWR directly via unreadCount
  
  // Dismissed action cards (persisted in localStorage)
  const [dismissedActions, setDismissedActions] = useState(() => {
    const saved = localStorage.getItem('dismissedActions');
    return saved ? JSON.parse(saved) : [];
  });
  
  const dismissAction = (actionId) => {
    const updated = [...dismissedActions, actionId];
    setDismissedActions(updated);
    localStorage.setItem('dismissedActions', JSON.stringify(updated));
  };
  
  // Add inspo modal
  const [showAddInspoModal, setShowAddInspoModal] = useState(false);
  const [lightboxInspo, setLightboxInspo] = useState(null);
  const [inspoSource, setInspoSource] = useState(null);
  const [newInspoUrl, setNewInspoUrl] = useState("");
  const [newInspoCategory, setNewInspoCategory] = useState("nails");
  const [addingInspo, setAddingInspo] = useState(false);
  const [activeInspoCategory, setActiveInspoCategory] = useState("all");
  const fileInputRef = useRef(null);
  const profilePhotoRef = useRef(null);
  
  // Inspo note editing state
  const [editingInspoNote, setEditingInspoNote] = useState(null);
  const [inspoNoteText, setInspoNoteText] = useState("");
  
  // Inspo category editing state
  const [editingInspoCategory, setEditingInspoCategory] = useState(null);
  
  // Inspo subfolder state
  const [inspoSubfolders, setInspoSubfolders] = useState({});
  const [activeSubfolder, setActiveSubfolder] = useState(null);
  const [newSubfolderName, setNewSubfolderName] = useState("");
  const [editingSubfolder, setEditingSubfolder] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // /me template content
  const [meTemplate, setMeTemplate] = useState({});
  
  // Delete confirmation state
  const [deletingInspo, setDeletingInspo] = useState(null);
  
  // Timezone detection and editing
  const [userTimezone, setUserTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "Unknown";
    }
  });
  const [editingTimezone, setEditingTimezone] = useState(false);
  
  // Common timezone options
  const TIMEZONE_OPTIONS = [
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Anchorage", label: "Alaska Time (AKT)" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
    { value: "Europe/London", label: "London (GMT/BST)" },
    { value: "Europe/Paris", label: "Paris (CET)" },
    { value: "Europe/Berlin", label: "Berlin (CET)" },
    { value: "Asia/Tokyo", label: "Tokyo (JST)" },
    { value: "Asia/Shanghai", label: "Shanghai (CST)" },
    { value: "Asia/Singapore", label: "Singapore (SGT)" },
    { value: "Australia/Sydney", label: "Sydney (AEST)" },
    { value: "Pacific/Auckland", label: "Auckland (NZST)" },
  ];
  
  // Save timezone to profile
  const saveTimezone = async (tz) => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/me/defaults/timezone`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ value: tz })
      });
      setUserTimezone(tz);
      setEditingTimezone(false);
      toast.success("Timezone updated!");
    } catch (err) {
      toast.error("Failed to save timezone");
    }
  };
  
  // Profile photo expand/crop state
  const [showExpandedPhoto, setShowExpandedPhoto] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [cropPreview, setCropPreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Delete account state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Event preference card preview modal
  const [showEventPreferencePreview, setShowEventPreferencePreview] = useState(false);
  const [previewingEvent, setPreviewingEvent] = useState(null);
  // Iter 9.40 Pass C - surface host notes (Pre/During/Post) in attending events
  const [hostNoteEvent, setHostNoteEvent] = useState(null);
  const [previewSettings, setPreviewSettings] = useState({
    show_name: true,
    show_photo: true,
    show_dietary: true,
    show_allergies: true,
    show_accessibility: true,
    show_invisible_disability: true
  });
  const [eventRsvpPreferences, setEventRsvpPreferences] = useState(null); // Stores the attendee's submitted preferences for previewing event
  const [showEditRsvpModal, setShowEditRsvpModal] = useState(false);
  const [editingRsvp, setEditingRsvp] = useState(null);
  const [isEditingEventPrefs, setIsEditingEventPrefs] = useState(false); // For inline editing in preview modal
  const [editingEventPrefsData, setEditingEventPrefsData] = useState(null); // Temporary edit state
  
  // Save edited event preferences
  const saveEventPreferences = async () => {
    if (!previewingEvent?.event_id || !editingEventPrefsData) return;
    
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/events/${previewingEvent.event_id}/preferences`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(editingEventPrefsData)
      });
      
      if (res.ok) {
        // Refresh the preferences
        await fetchEventRsvpPreferences(previewingEvent.event_id);
        setIsEditingEventPrefs(false);
        toast.success("Preferences updated!");
      } else {
        toast.error("Failed to save preferences");
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error("Failed to save preferences");
    }
  };
  
  // Human-readable labels for preference values
  const PREFERENCE_LABELS = {
    // Accessibility
    wheelchair_accessible: { label: "Wheelchair Accessible", desc: "Requires wheelchair-accessible seating and pathways" },
    extra_time_needed: { label: "Extra Time Needed", desc: "May need additional time for activities" },
    hearing_assistance: { label: "Hearing Assistance", desc: "Requires hearing accommodations" },
    service_animal: { label: "Service Animal", desc: "Accompanied by a service animal" },
    fragrance_free: { label: "Fragrance-Free", desc: "Sensitive to fragrances and perfumes" },
    limited_mobility: { label: "Limited Mobility", desc: "May have difficulty with extended walking or standing" },
    // Invisible disabilities
    vocal_tics: { label: "Vocal Tics", desc: "May have involuntary vocal expressions" },
    motor_tics: { label: "Motor Tics", desc: "May have involuntary motor movements" },
    tourettes: { label: "Tourette's Syndrome", desc: "Has Tourette's Syndrome" },
    anxiety: { label: "Anxiety", desc: "Experiences anxiety in certain situations" },
    chronic_pain: { label: "Chronic Pain", desc: "Lives with chronic pain conditions" },
    chronic_fatigue: { label: "Chronic Fatigue", desc: "Experiences chronic fatigue" },
    adhd: { label: "ADHD", desc: "Has attention deficit hyperactivity disorder" },
    autism: { label: "Autism Spectrum", desc: "On the autism spectrum" },
    epilepsy: { label: "Epilepsy", desc: "Has epilepsy" },
    ptsd: { label: "PTSD", desc: "Has post-traumatic stress disorder" },
    // Dietary
    vegan: { label: "Vegan", desc: "No animal products" },
    vegetarian: { label: "Vegetarian", desc: "No meat" },
    gluten_free: { label: "Gluten-Free", desc: "No gluten" },
    dairy_free: { label: "Dairy-Free", desc: "No dairy" },
    nut_free: { label: "Nut-Free", desc: "No nuts" },
    kosher: { label: "Kosher", desc: "Kosher dietary requirements" },
    halal: { label: "Halal", desc: "Halal dietary requirements" },
    pescatarian: { label: "Pescatarian", desc: "Fish but no meat" },
    // Seating
    front_row: { label: "Front Row", desc: "Prefers seating at the front" },
    near_exit: { label: "Near Exit", desc: "Prefers seating near an exit" },
    near_hosts: { label: "Near Hosts", desc: "Prefers seating near the hosts" },
    near_action: { label: "Near the Action", desc: "Prefers seating close to main activities" },
    quiet_area: { label: "Quiet Area", desc: "Prefers a quieter seating area" },
    accessible_seating: { label: "Accessible Seating", desc: "Requires accessible seating" },
    // Legacy mappings (deprecated values)
    near_stage: { label: "Near the Action", desc: "Prefers seating close to main activities" },
    // Vibe
    very_social: { label: "Very Social", desc: "Loves meeting new people" },
    selectively_social: { label: "Selectively Social", desc: "Enjoys smaller, meaningful conversations" },
    observer: { label: "Observer", desc: "Prefers to watch and take it all in" },
    networking: { label: "Networking", desc: "Focused on making professional connections" }
  };
  
  // Helper to get human-readable label
  const getPreferenceLabel = (value) => {
    const key = value?.toLowerCase?.()?.replace(/[\s-]/g, '_') || value;
    return PREFERENCE_LABELS[key]?.label || value?.replace?.(/_/g, ' ')?.replace?.(/\b\w/g, l => l.toUpperCase()) || value;
  };
  
  const getPreferenceDesc = (value) => {
    const key = value?.toLowerCase?.()?.replace(/[\s-]/g, '_') || value;
    return PREFERENCE_LABELS[key]?.desc || '';
  };
  
  // Fetch event RSVP preferences when previewing
  const fetchEventRsvpPreferences = async (eventId) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/events/${eventId}/my-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEventRsvpPreferences(data);
        return data;
      }
    } catch (err) {
      console.error('Failed to fetch event preferences:', err);
    }
    return null;
  };
  
  // Update RSVP status (can't make it)
  const updateRsvpStatus = async (eventId, rsvpId, newStatus) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/events/${eventId}/rsvps/${rsvpId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        // Revalidate attending events via SWR instead of manual fetch
        await revalidateAll();
        setShowEditRsvpModal(false);
        return true;
      }
    } catch (err) {
      console.error('Failed to update RSVP:', err);
    }
    return false;
  };
  
  // Experience preview modal (for experiences with dates)
  const [showExperiencePreview, setShowExperiencePreview] = useState(false);
  const [previewingExperience, setPreviewingExperience] = useState(null);
  
  // Edit event defaults modal (combined - kept for backwards compatibility)
  const [showEditEventDefaults, setShowEditEventDefaults] = useState(false);
  
  // Separate modals for each default category
  const [showEventsDefaultModal, setShowEventsDefaultModal] = useState(false);
  const [showAccessibilityDefaultModal, setShowAccessibilityDefaultModal] = useState(false);
  const [showDietaryDefaultModal, setShowDietaryDefaultModal] = useState(false);
  const [showInterestsDefaultModal, setShowInterestsDefaultModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Handle crop complete
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);
  
  // Create cropped image blob
  const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
    
    canvas.width = safeArea;
    canvas.height = safeArea;
    
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);
    
    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    );
    
    const data = ctx.getImageData(0, 0, safeArea, safeArea);
    
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    
    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };
  
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });
  
  // Profile photo upload - with crop option
  const handleProfilePhotoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Create preview URL for cropping
    const previewUrl = URL.createObjectURL(file);
    setSelectedPhotoFile(file);
    setCropPreview(previewUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setShowCropModal(true);
  };
  
  // Upload the cropped photo
  const uploadProfilePhoto = async () => {
    if (!cropPreview || !croppedAreaPixels) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const croppedBlob = await getCroppedImg(cropPreview, croppedAreaPixels, rotation);
      const formData = new FormData();
      formData.append('file', croppedBlob, 'profile.jpg');
      
      const res = await fetch(`${API}/user/profile-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        // Update both field names for consistency across components
        setUser({...user, profile_photo_url: data.photo_url, profile_image: data.photo_url});
        toast.success("Profile photo updated!");
        setShowCropModal(false);
        setSelectedPhotoFile(null);
        setCropPreview(null);
      } else {
        toast.error("Failed to upload photo");
      }
    } catch (err) {
      toast.error("Failed to upload photo");
    }
  };
  
  // Save profile function
  const saveProfile = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/user/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editProfileData)
      });
      if (res.ok) {
        setUser({...user, ...editProfileData});
        setShowEditProfileModal(false);
        toast.success("Profile updated!");
      }
    } catch (err) {
      toast.error("Failed to save profile");
    }
  };
  
  // Note: organizations come from SWR directly
  
  // Settings state
  const [hideUpgradeSection, setHideUpgradeSection] = useState(() => {
    return localStorage.getItem("phygital_hide_upgrade") === "true";
  });
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingBirthday, setEditingBirthday] = useState(false);
  const [birthday, setBirthday] = useState({ month: "", day: "", year: "" });
  const [copiedTokenId, setCopiedTokenId] = useState(false);
  
  // Defaults tab state (lifted to prevent focus loss)
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingPrefs, setEditingPrefs] = useState({});
  const [expandedAccessibility, setExpandedAccessibility] = useState({});
  
  // Persist hideUpgradeSection
  const dismissUpgradeSection = () => {
    setHideUpgradeSection(true);
    localStorage.setItem("phygital_hide_upgrade", "true");
  };


  // Use SWR hooks for data fetching with automatic sync
  const { 
    user: swrUser, 
    preferences: swrPreferences, 
    defaults: swrDefaults,
    experiences: swrExperiences, 
    inspirations: swrInspirations, 
    hostedEvents: swrHostedEvents,
    attendingEvents: swrAttendingEvents,
    organizations: swrOrganizations,
    notifications: swrNotifications,
    unreadCount,
    isLoading: swrLoading,
    revalidateAll,
    mutateUser
  } = useAllUserData();

  // Initialize user state ONCE from SWR data - avoid re-render loops
  // Only update local state when swrUser changes AND local user is null or different user_id
  useEffect(() => {
    if (swrUser && (!user || user.user_id !== swrUser.user_id)) {
      setUser(swrUser);
      if (swrUser.card_settings) {
        setCardSettings(swrUser.card_settings);
      }
    }
  }, [swrUser?.user_id]); // Only depend on user_id, not entire object

  // Initialize preferences ONCE
  useEffect(() => {
    if (swrPreferences && Object.keys(swrPreferences).length && !Object.keys(preferences).length) {
      setPreferences(swrPreferences);
    }
  }, [swrPreferences?.updated_at]); // Only update when preferences actually change

  // Use SWR data directly for experiences, inspirations, events instead of syncing to state
  // This removes the re-render loop entirely
  const experiences = swrExperiences || [];
  const inspirations = swrInspirations || [];
  const hostedEvents = swrHostedEvents || [];
  const attendingEvents = swrAttendingEvents || [];
  const organizations = swrOrganizations || [];
  const notifications = swrNotifications || [];

  // Defaults - only initialize once
  useEffect(() => {
    if (swrDefaults && !userDefaults) {
      setUserDefaults(swrDefaults);
      setDefaultsLoading(false);
    }
  }, [swrDefaults?.updated_at]);

  // Update loading state - only on initial load, not on revalidation
  useEffect(() => {
    if (!swrLoading && loading) {
      setLoading(false);
      setDefaultsLoading(false);
    }
  }, [swrLoading, loading]);

  useEffect(() => { 
    document.title = "phygital - My Dashboard";
    const token = localStorage.getItem("session_token");
    if (!token) navigate("/login");
    
    // Fetch inspo subfolders
    if (token) {
      fetch(`${API}/me/inspo-subfolders`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { folders: {} })
        .then(d => setInspoSubfolders(d.folders || {}))
        .catch(() => {});
      
      // Fetch /me template + company settings for hosting CTA
      Promise.all([
        fetch(`${API}/me-template`).then(r => r.ok ? r.json() : { sections: {} }),
        fetch(`${API}/company-settings`).then(r => r.ok ? r.json() : {})
      ]).then(([templateData, companyData]) => {
        const sections = templateData.sections || {};
        // Company settings is the source of truth for hosting CTA
        sections.hosting_cta_title = companyData.hosting_cta_title || "";
        sections.hosting_cta_description = companyData.hosting_cta_description || "";
        sections.hosting_cta_button = companyData.hosting_cta_button || "";
        setMeTemplate(sections);
      }).catch(() => {});
      
      // Check if user has an investor portal
      fetch(`${API}/user/my-investor-portal`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { has_portal: false })
        .then(d => { if (d.has_portal) setInvestorPortal(d); })
        .catch(() => {});
    }
  }, [navigate]);

  // Keep fetchUserData for manual refresh but now use SWR revalidation
  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("session_token");
      if (!token) { navigate("/login"); return; }
      // Trigger SWR revalidation for all data
      await revalidateAllUserData();
    } catch (err) { 
      console.error("Error refreshing user data:", err); 
    }
  };

  // Delete account handler
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE") {
      toast.error("Type 'DELETE' to confirm");
      return;
    }
    
    setDeletingAccount(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/user/delete-account`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          confirmation: deleteConfirmation,
          reason: deleteReason 
        })
      });
      
      if (res.ok) {
        toast.success("Your account has been permanently deleted");
        localStorage.removeItem("session_token");
        logout();
        navigate("/");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to delete account");
      }
    } catch (err) {
      toast.error("Failed to delete account");
    }
    setDeletingAccount(false);
  };

  const saveCardSettings = async (settings) => {
    const token = localStorage.getItem("session_token");
    const res = await fetch(`${API}/user/card-settings`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settings)
    });
    if (res.ok) {
      const data = await res.json();
      setCardSettings(data.card_settings);
    } else {
      throw new Error("Failed to save");
    }
  };

  const copyProfileLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${user?.share_id || user?.user_id}`);
    setCopied(true); toast.success("Profile link copied!"); setTimeout(() => setCopied(false), 2000);
  };

  const getCategoriesWithInspo = () => {
    const cats = new Set();
    inspirations.forEach(i => cats.add(i.category || 'nails'));
    return Array.from(cats);
  };

  // Filter experiences
  const scheduledExperiences = experiences.filter(e => e.target_date);
  const unscheduledExperiences = experiences.filter(e => !e.target_date);
  const bookedExperiences = experiences.filter(e => e.status === 'booked' || e.status === 'confirmed' || e.provider_id);

  // Get experiences for a specific date
  const getExperiencesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return experiences.filter(e => e.target_date === dateStr);
  };

  // Dates with experiences (for calendar highlighting)
  const datesWithExperiences = experiences
    .filter(e => e.target_date)
    .map(e => new Date(e.target_date));

  const createExperience = async () => {
    if (!newExperience.name) { toast.error("Please give your experience a name"); return; }
    setCreatingExperience(true);
    try {
      const token = localStorage.getItem("session_token");
      const payload = {
        ...newExperience,
        inspiration_ids: selectedInspos.map(i => i.inspiration_id),
        target_date: selectedDate ? selectedDate.toISOString().split('T')[0] : newExperience.target_date,
        look_status: newExperience.look_status || "decided"
      };
      const res = await fetch(`${API}/experiences`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success("Experience created!");
        setShowCreateModal(false);
        setSelectedCategory(null);
        setSelectedDate(null);
        setNewExperience({ name: "", category: "nails", target_date: "", stylist_note: "", inspiration_ids: [], custom_adjustments: {}, look_status: "decided" });
        setSelectedInspos([]);
        fetchUserData();
      }
    } catch (err) { toast.error("Failed to create experience"); }
    finally { setCreatingExperience(false); }
  };

  const updateExperience = async (notifyProvider = false) => {
    if (!editingExperience) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/experiences/${editingExperience.experience_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editingExperience.name,
          category: editingExperience.category,
          target_date: editingExperience.target_date,
          appointment_time: editingExperience.appointment_time,
          stylist_note: editingExperience.stylist_note,
          inspiration_ids: editingExperience.inspiration_ids || [],
          custom_adjustments: editingExperience.custom_adjustments || {},
          provider_info: editingExperience.provider_info,
          notify_provider: notifyProvider
        })
      });
      if (res.ok) {
        toast.success(notifyProvider ? "Experience updated & provider notified!" : "Experience updated!");
        setShowEditModal(false);
        setEditingExperience(null);
        fetchUserData();
      }
    } catch (err) { toast.error("Failed to update experience"); }
  };

  // Share experience update link with provider
  const shareExperienceUpdate = async (experienceId) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/experiences/${experienceId}/share-update`, { 
        method: "POST", 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (res.ok) { 
        const data = await res.json(); 
        navigator.clipboard.writeText(`Your client has updated their experience: ${data.share_url}`); 
        toast.success("Update notification link copied! Share with your provider."); 
      }
    } catch (err) { 
      // Fallback - just copy the share link
      shareExperience(experienceId);
    }
  };

  const shareExperience = async (experienceId) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/experiences/${experienceId}/share`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); navigator.clipboard.writeText(data.share_url); toast.success("Share link copied!"); fetchUserData(); }
    } catch (err) { toast.error("Failed to generate share link"); }
  };

  const deleteExperience = async (experienceId) => {
    try {
      setDeletingExpInFlight(true);
      const token = localStorage.getItem("session_token");
      // Iter 9.47.q - use unified DELETE that removes from both user_planned_experiences AND experiences
      const res = await fetch(`${API}/user/experiences/${experienceId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        toast.success("Experience deleted");
        setDeletingExperience(null);
        fetchUserData();
      } else {
        toast.error("Failed to delete experience");
      }
    } catch (err) { toast.error("Failed to delete experience"); }
    finally { setDeletingExpInFlight(false); }
  };

  const linkProvider = async () => {
    if (!linkingExperience) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/experiences/${linkingExperience.experience_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...linkingExperience,
          provider_info: providerInfo,
          status: "booked"
        })
      });
      if (res.ok) {
        toast.success("Provider linked!");
        setShowLinkProviderModal(false);
        setLinkingExperience(null);
        setProviderInfo({ name: "", phone: "", email: "", website: "", instagram: "" });
        fetchUserData();
      }
    } catch (err) { toast.error("Failed to link provider"); }
  };

  // Provider search functions
  const searchProviders = async (query) => {
    if (!query || query.length < 2) {
      setProviderSearchResults([]);
      return;
    }
    setSearchingProviders(true);
    try {
      const res = await fetch(`${API}/providers/search?q=${encodeURIComponent(query)}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        setProviderSearchResults(data.providers || []);
      }
    } catch (err) {
      console.error("Provider search failed:", err);
    } finally {
      setSearchingProviders(false);
    }
  };

  const shareWalletWithProvider = async (provider) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/wallet/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          provider_id: provider.provider_id,
          categories: ["hair", "nails", "makeup", "lash"],
          message: `Hi! I'd love to share my preferences with you.`
        })
      });
      if (res.ok) {
        toast.success(`Wallet shared with ${provider.business_name}!`);
        setShowProviderSearchModal(false);
        setProviderSearchQuery("");
        setProviderSearchResults([]);
      } else {
        toast.error("Failed to share wallet");
      }
    } catch (err) {
      toast.error("Failed to share wallet");
    }
  };

  // Notifications are now handled via SWR hook - no manual fetch needed
  // The useNotifications() hook in useAllUserData already polls every 30 seconds

  // Create experience from category (defaults/inspo)
  const startExperienceFromCategory = (category, fromInspo = false) => {
    // Get category inspo if available
    const categoryInspo = inspirations.filter(i => i.category === category).map(i => i.inspiration_id);
    setNewExperience({
      name: "",
      category: category,
      target_date: "",
      stylist_note: "",
      inspiration_ids: categoryInspo,
      custom_adjustments: {},
      no_date_yet: true,
      look_status: "exploring"
    });
    setShowCreateModal(true);
    if (fromInspo) {
      toast.info("Creating experience with your saved inspirations");
    } else {
      toast.info("Creating experience from your defaults");
    }
  };

  // Check if URL is an Instagram post
  const isInstagramUrl = (url) => {
    return url && (url.includes('instagram.com/p/') || url.includes('instagram.com/reel/'));
  };

  // Extract Instagram post ID from URL
  const extractInstagramId = (url) => {
    const match = url.match(/instagram\.com\/(p|reel)\/([A-Za-z0-9_-]+)/);
    return match ? match[2] : null;
  };

  const addInspiration = async (imageUrl, source = 'upload') => {
    setAddingInspo(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // Check if it's an Instagram URL - store as embed type
      const isIgEmbed = isInstagramUrl(imageUrl);
      const igPostId = isIgEmbed ? extractInstagramId(imageUrl) : null;
      
      const res = await fetch(`${API}/inspirations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ 
          image_url: imageUrl, 
          source: isIgEmbed ? 'instagram_embed' : source, 
          category: newInspoCategory,
          embed_type: isIgEmbed ? 'instagram' : null,
          embed_id: igPostId
        })
      });
      if (res.ok) {
        toast.success(isIgEmbed ? "Instagram post embedded!" : "Inspiration added!");
        setShowAddInspoModal(false);
        setInspoSource(null);
        setNewInspoUrl("");
        fetchUserData();
      }
    } catch (err) { toast.error("Failed to add inspiration"); }
    finally { setAddingInspo(false); }
  };

  const deleteInspiration = async (inspirationId) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/inspirations/${inspirationId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { 
        toast.success("Inspiration deleted"); 
        setDeletingInspo(null);
        fetchUserData(); 
      }
    } catch (err) { toast.error("Failed to delete inspiration"); }
  };
  
  // Create experience from selected inspo
  const createExperienceFromInspo = (inspo) => {
    setSelectedInspos([inspo]);
    startCreateExperience(inspo.category || 'nails');
  };

  // Save note for inspiration
  const saveInspoNote = async (inspirationId, note) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/inspirations/${inspirationId}`, { 
        method: "PUT", 
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note })
      });
      if (res.ok) { 
        toast.success("Note saved"); 
        setEditingInspoNote(null);
        setInspoNoteText("");
        fetchUserData(); 
      }
    } catch (err) { toast.error("Failed to save note"); }
  };
  
  // Update inspo category (tag)
  const updateInspoCategory = async (inspirationId, newCategory) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/inspirations/${inspirationId}`, { 
        method: "PUT", 
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category: newCategory })
      });
      if (res.ok) { 
        toast.success("Category updated"); 
        setEditingInspoCategory(null);
        // Revalidate via SWR instead of optimistic update to avoid re-render loops
        await revalidateAll();
      }
    } catch (err) { toast.error("Failed to update category"); }
  };

  const toggleInspoSelection = (insp) => {
    if (selectedInspos.find(i => i.inspiration_id === insp.inspiration_id)) {
      setSelectedInspos(selectedInspos.filter(i => i.inspiration_id !== insp.inspiration_id));
    } else { setSelectedInspos([...selectedInspos, insp]); }
  };

  // Handle inspo file upload
  const handleInspoFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setAddingInspo(true);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', newInspoCategory || 'nails');
      
      const res = await fetch(`${API}/inspirations/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success("Photo uploaded!");
        setShowAddInspoModal(false);
        fetchUserData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to upload");
      }
    } catch (err) {
      toast.error("Failed to upload photo");
    } finally {
      setAddingInspo(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getCategoryProgress = (cat) => {
    const prefs = preferences[cat];
    if (!prefs) return 0;
    const fields = Object.values(prefs).filter(v => v !== null && v !== "" && (!Array.isArray(v) || v.length > 0));
    return Math.min(100, fields.length * 15);
  };

  const startCreateExperience = (category, date = null) => {
    setSelectedCategory(category);
    setSelectedDate(date);
    setCreateStep(1); // Reset to step 1
    setNewExperience({ 
      name: "", 
      category, 
      target_date: date ? date.toISOString().split('T')[0] : "", 
      stylist_note: "", 
      inspiration_ids: [], 
      custom_adjustments: {}, 
      no_date_yet: false,
      occasion: ""
    });
    setShowCreateModal(true);
  };

  const openEditModal = (exp) => {
    setEditingExperience({ ...exp });
    setShowEditModal(true);
  };

  const openLinkProviderModal = (exp) => {
    setLinkingExperience(exp);
    setProviderInfo(exp.provider_info || { name: "", phone: "", email: "", website: "", instagram: "" });
    setShowLinkProviderModal(true);
  };

  // Experience filter state
  const [experienceFilter, setExperienceFilter] = useState("all"); // all, draft, booked

  // Filtered experiences
  const filteredExperiences = experiences.filter(exp => {
    if (experienceFilter === "all") return true;
    if (experienceFilter === "draft") return exp.status === "draft" || exp.status === "ready";
    if (experienceFilter === "booked") return exp.status === "booked" || exp.status === "confirmed" || exp.provider_info?.name;
    return true;
  });

  // Experience Card Component with Image Cover
  const ExperienceCard = ({ exp, showActions = true }) => {
    const catConfig = CATEGORIES[exp.category] || CATEGORIES.nails;
    const CategoryIcon = catConfig.icon;
    const statusConfig = STATUS_CONFIG[exp.status] || STATUS_CONFIG.draft;
    // Use getCoverImage to properly handle Instagram embeds
    const coverImage = getCoverImage(exp);
    
    return (
      <Card className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all" data-testid={`experience-card-${exp.experience_id}`}>
        {/* Cover Image */}
        <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200">
          {coverImage ? (
            <img src={coverImage} alt={exp.name} className="w-full h-full object-cover" />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${catConfig.lightGradient} flex items-center justify-center`}>
              <CategoryIcon className={`w-12 h-12 ${catConfig.color.split(' ')[1]} opacity-50`} />
            </div>
          )}
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Status badge */}
          <div className="absolute top-3 right-3 flex items-center gap-1">
            <Badge className={`${statusConfig.color} text-xs`}>{statusConfig.label}</Badge>
          </div>
          
          {/* Category badge */}
          <div className="absolute top-3 left-3">
            <Badge variant="secondary" className="bg-white/90 text-xs gap-1">
              <CategoryIcon className="w-3 h-3" />
              {catConfig.name}
            </Badge>
          </div>
          
          {/* Delete button on hover */}
          {showActions && (
            <button
              onClick={(e) => { e.stopPropagation(); setDeletingExperience(exp); }}
              data-testid={`experience-delete-btn-${exp.experience_id}`}
              className="absolute top-3 right-20 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full"
              title="Delete experience"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          
          {/* Title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-semibold text-white text-lg mb-1 line-clamp-1">{exp.name}</h3>
            {exp.target_date && (
              <p className="text-white/80 text-sm flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" />
                {new Date(exp.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            )}
          </div>
          
          {/* Multiple images indicator */}
          {exp.inspiration_images?.length > 1 && (
            <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
              +{exp.inspiration_images.length - 1}
            </div>
          )}
        </div>
        
        {/* Card Footer */}
        {showActions && (
          <CardContent className="p-3">
            {/* Default preferences info */}
            {exp.preferences_override && Object.keys(exp.preferences_override).length > 0 && (
              <div className="flex items-center gap-2 mb-2 text-xs text-gray-600">
                <Settings className="w-3 h-3" />
                <span>Uses your {catConfig.name} defaults + adjustments</span>
              </div>
            )}
            
            {/* Provider info if linked */}
            {exp.provider_info?.name && (
              <div className="flex items-center gap-2 mb-3 p-2 bg-green-50 rounded-lg">
                <User className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">{exp.provider_info.name}</span>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEditModal(exp)} className="flex-1">
                <Edit3 className="w-3 h-3 mr-1" />Edit
              </Button>
              {/* Move-to-occasion dropdown (functional alternative to drag-drop) */}
              <select
                value={exp.linked_event_id || ""}
                onClick={(e) => e.stopPropagation()}
                onChange={async (e) => {
                  e.stopPropagation();
                  const newOcc = e.target.value || null;
                  try {
                    const token = localStorage.getItem("session_token");
                    const r = await fetch(`${API}/api/user/experiences/${exp.experience_id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ linked_event_id: newOcc })
                    });
                    if (r.ok) {
                      toast.success(newOcc ? "Linked to occasion" : "Unlinked");
                      revalidateAll?.();
                    }
                  } catch { /* silent */ }
                }}
                className="text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1 max-w-[120px] truncate"
                data-testid={`experiences-move-to-occasion-${exp.experience_id}`}
                title="Move to occasion"
              >
                <option value="">No occasion</option>
                {experiences.filter(e2 => e2.type === "personal_event" || e2.event_type).map(occ => (
                  <option key={occ.experience_id} value={occ.experience_id}>{occ.name}</option>
                ))}
              </select>
              <Button size="sm" onClick={() => shareExperience(exp.experience_id)} className="flex-1">
                <Share className="w-3 h-3 mr-1" />Share
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full" /></div>;

  const categoriesWithInspo = getCategoriesWithInspo();
  const hasAnyInspo = inspirations.length > 0;

  // Mobile Home Tab Content
  const MobileHomeTab = () => {
    const bookedExperiences = experiences.filter(e => e.status === 'booked' || e.provider_info?.name);
    
    // Determine if user is a Host or Participant
    // Hosts: Have role_host tag, or have hosted events, or have organizations, or are admin
    const userTags = user?.tags || [];
    const isHost = useMemo(() => {
      const hasHostTag = userTags.includes('role_host') || userTags.includes('host');
      // Iter 9.40 Slice I.f - also accept native role:'host' (was missing - caused Aria's
      // hosting tile to not surface in /me even though her event existed).
      const hasHostRole = user?.role === 'host';
      const hasAdminRole = user?.role === 'admin' || user?.role === 'super_admin';
      const isHostFlag = user?.is_host === true;
      const hasHostedEvents = hostedEvents && hostedEvents.length > 0;
      const hasOrganizations = organizations && organizations.length > 0;
      const hasPilotTag = userTags.some(t => t.startsWith('pilot_'));
      return hasHostTag || hasHostRole || isHostFlag || hasAdminRole || hasHostedEvents || hasOrganizations || hasPilotTag;
    }, [userTags, user?.role, user?.is_host, hostedEvents, organizations]);
    
    // Check if user is in the Event pilot specifically (can create events)
    const isEventPilot = useMemo(() => {
      const hasEventPilotTag = userTags.includes('pilot_events') || userTags.includes('pilot_instructor');
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      return hasEventPilotTag || isAdmin || isHost;
    }, [userTags, user?.role, isHost]);
    
    // Check defaults
    const hasDietaryDefaults = userDefaults?.defaults?.dining || userDefaults?.defaults?.dietary || userDefaults?.filled_categories?.includes('dietary');
    const hasAccessibilityDefaults = userDefaults?.defaults?.accessibility || userDefaults?.filled_categories?.includes('accessibility');
    
    return (
    <div className="space-y-4">
      {/* Simplified PHY Card - Clean & Minimal */}
      <Card 
        className="text-white border-0 rounded-2xl overflow-hidden relative"
        style={{ backgroundColor: walletColor }}
        data-testid="preference-card"
      >
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")'}} />
        <CardContent className="p-4 relative">
          <div className="flex items-center justify-between">
            {/* Left - Avatar + Info */}
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-full overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/40 transition-all"
                onClick={() => (user?.profile_image || user?.profile_photo_url) ? setShowExpandedPhoto(true) : profilePhotoRef.current?.click()}
              >
                {/* R5: profile_image is canonical, profile_photo_url legacy fallback. Initials only when neither exists. */}
                {(user?.profile_image || user?.profile_photo_url) ? (
                  <img src={user.profile_image || user.profile_photo_url} alt="" className="w-full h-full object-cover" data-testid="phy-card-avatar" />
                ) : (
                  <DefaultAvatar 
                    firstName={user?.first_name || user?.name?.split(' ')[0]} 
                    lastName={user?.last_name || user?.name?.split(' ')[1]} 
                    size={48} 
                  />
                )}
              </div>
              <div>
                <h1 className="text-lg font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}` 
                    : user?.name || "Guest"}
                </h1>
                <p className="text-white/70 text-xs">{user?.wallet_id || "PHY-XXXX"}</p>
              </div>
            </div>
            
            {/* Right - Quick Actions */}
            <div className="flex items-center gap-2">
              {/* Share button opens preference card list */}
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-white bg-white/20 hover:bg-white/30 hover:text-white h-8 rounded-full px-3 text-xs"
                onClick={() => setShowSharePreferenceCard(true)}
                data-testid="share-preference-card-btn"
              >
                <ArrowUpFromLine className="w-3.5 h-3.5 mr-1" />
                Share
              </Button>
            </div>
          </div>
          
          {/* Quick Stats Bar - Removed Dietary/Accessibility as per user request */}
          <div className="mt-3 pt-3 border-t border-white/10">
            {/* Empty - removed Edit Defaults link */}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Section */}
      {(() => {
        // Deduplicate events using a Map to ensure unique event_ids
        const eventMap = new Map();
        hostedEvents.forEach(e => {
          if (e.event_id && !eventMap.has(e.event_id)) {
            eventMap.set(e.event_id, {...e, isHosting: true});
          }
        });
        attendingEvents.forEach(e => {
          if (e.event_id) {
            if (eventMap.has(e.event_id)) {
              // Mark as both hosting and attending
              eventMap.set(e.event_id, { ...eventMap.get(e.event_id), isAttending: true });
            } else {
              eventMap.set(e.event_id, { ...e, isAttending: true, isHosting: false });
            }
          }
        });
        const allEvents = Array.from(eventMap.values());
        const eventsWithDates = allEvents.filter(e => e.date);
        const experiencesWithDates = experiences.filter(e => e.target_date && !e.no_date_yet);
        const hasUpcomingItems = eventsWithDates.length > 0 || experiencesWithDates.length > 0;
        
        if (!hasUpcomingItems) {
          return (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-sm text-gray-800" data-testid="me-upcoming-title">{upcomingSectionTitle}</h2>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {/* Plan a Service tile - Iter 9.40 Slice C⁶: open modal in-place (no navigation away from Dashboard). */}
                <button
                  onClick={() => setShowPlanService(true)}
                  className="p-4 rounded-xl border text-left hover:shadow-sm transition-all"
                  style={{ backgroundColor: planCtaService.bgColor, borderColor: `${planCtaService.iconColor}33` }}
                  data-testid="plan-experience-btn"
                >
                  {(() => {
                    const ServiceIcon = planCtaService.icon;
                    return <ServiceIcon className="w-5 h-5 mb-2" style={{ color: planCtaService.iconColor }} />;
                  })()}
                  <p className="font-medium text-sm text-gray-800">{planCtaService.title}</p>
                  <p className="text-[10px] text-gray-500">{planCtaService.subtitle}</p>
                </button>
                {/* Plan around an Occasion tile - also opens in-place modal */}
                <button
                  onClick={() => setShowPlanOccasion(true)}
                  className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-left hover:shadow-sm transition-all"
                  data-testid="plan-event-btn"
                >
                  {(() => {
                    const OccIcon = planCtaOccasion.icon;
                    return <OccIcon className="w-5 h-5 text-gray-700 mb-2" />;
                  })()}
                  <p className="font-medium text-sm text-gray-800">{planCtaOccasion.title}</p>
                  <p className="text-[10px] text-gray-500">{planCtaOccasion.subtitle}</p>
                </button>
              </div>
            </div>
          );
        }
        
        return (
          <div>
            {/* Iter 9.40 Slice C⁶ - Plan tiles also visible above upcoming items so users can plan
                more services/occasions without scrolling away or navigating off the dashboard. */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setShowPlanService(true)}
                className="p-3 rounded-xl border text-left hover:shadow-sm transition-all"
                style={{ backgroundColor: planCtaService.bgColor, borderColor: `${planCtaService.iconColor}33` }}
                data-testid="plan-experience-btn"
              >
                {(() => {
                  const ServiceIcon = planCtaService.icon;
                  return <ServiceIcon className="w-4 h-4 mb-1.5" style={{ color: planCtaService.iconColor }} />;
                })()}
                <p className="font-medium text-xs text-gray-800">{planCtaService.title}</p>
              </button>
              <button
                onClick={() => setShowPlanOccasion(true)}
                className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-left hover:shadow-sm transition-all"
                data-testid="plan-event-btn"
              >
                {(() => {
                  const OccIcon = planCtaOccasion.icon;
                  return <OccIcon className="w-4 h-4 text-gray-700 mb-1.5" />;
                })()}
                <p className="font-medium text-xs text-gray-800">{planCtaOccasion.title}</p>
              </button>
            </div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-sm text-gray-800" data-testid="me-upcoming-title">{upcomingSectionTitle}</h2>
              <Button variant="ghost" size="sm" className="text-gray-500 h-7 text-xs" onClick={() => navigate("/experiences")}>
                View All
              </Button>
            </div>
            <div className="space-y-2">
              {/* Events */}
              {eventsWithDates.slice(0, 3).map((event) => (
                <Card 
                  key={event.event_id}
                  className="rounded-xl border-gray-100 shadow-sm hover:shadow-md transition-all"
                  data-testid={`event-card-${event.event_id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <div 
                      className="w-11 h-11 rounded-lg overflow-hidden bg-black flex-shrink-0 cursor-pointer"
                      onClick={() => {
                        if (event.isHosting) navigate(`/host/v2/${event.event_id}`);
                        else navigate(`/e/${event.slug || event.event_id}`);
                      }}
                    >
                      {(event.cover_image || event.logo_image || event.image) ? (
                        <img src={getImageUrl(event.cover_image || event.logo_image || event.image)} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <div className="w-full h-full bg-purple-100 flex items-center justify-center">
                          <PartyPopper className="w-5 h-5 text-purple-600" />
                        </div>
                      )}
                    </div>
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => {
                        if (event.isHosting) navigate(`/host/v2/${event.event_id}`);
                        else if (event.isAttending) navigate(`/e/${event.slug || event.event_id}?pass=true`);
                        else navigate(`/e/${event.slug || event.event_id}`);
                      }}
                    >
                      <div>
                        <h4 className="font-medium text-sm truncate">{event.name}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-xs text-gray-500">
                            {(() => {
                              // Parse date properly to avoid timezone issues
                              const dateParts = (event.date || '').split('T')[0].split('-').map(Number);
                              const timeParts = (event.start_time || event.time || '00:00').split(':').map(Number);
                              if (dateParts.length === 3 && dateParts[0] > 0) {
                                const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2], timeParts[0] || 0, timeParts[1] || 0);
                                const dateStr = dateObj.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'});
                                const timeStr = dateObj.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});
                                const tz = event.timezone ? event.timezone.split('/')[1]?.replace('_', ' ') : 'Local';
                                return `${dateStr} • ${timeStr} ${tz}`;
                              }
                              return 'Date TBD';
                            })()}
                          </p>
                          {event.isHosting && <Badge className="text-[9px] bg-violet-100 text-violet-700 px-1">Host</Badge>}
                          {event.isAttending && <Badge className="text-[9px] bg-purple-100 text-purple-700 px-1">RSVP</Badge>}
                        </div>
                      </div>
                    </div>
                    {/* Quick link to confirmation/event page - if RSVP'd go to confirmation */}
                    <div className="flex items-center gap-2 relative z-10">
                      {/* Preview Eye for preference card - only if RSVP'd */}
                      {event.isAttending && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          // Fetch event-specific RSVP preferences before opening preview
                          try {
                            const token = localStorage.getItem("session_token");
                            const res = await fetch(`${API}/events/${event.event_id}/my-rsvp`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const rsvpData = await res.json();
                              // Merge RSVP preferences into event for preview
                              setPreviewEvent({
                                ...event,
                                rsvpPreferences: {
                                  accessibility: {
                                    accessibility_needs: rsvpData.accessibility_needs || [],
                                    invisible_disabilities: rsvpData.invisible_disabilities || [],
                                    notes: rsvpData.additional_notes || rsvpData.accessibility_notes
                                  },
                                  dietary: {
                                    dietary_restrictions: rsvpData.dietary_restrictions || [],
                                    allergies: rsvpData.allergies || [],
                                    notes: rsvpData.dietary_notes
                                  },
                                  interests: rsvpData.interests || [],
                                  vibe: rsvpData.vibe
                                }
                              });
                            } else {
                              // No RSVP found, use user's defaults
                              setPreviewEvent(event);
                            }
                          } catch (err) {
                            console.error("Failed to fetch RSVP:", err);
                            setPreviewEvent(event);
                          }
                        }}
                        className="w-9 h-9 rounded-full bg-purple-50 hover:bg-purple-100 flex items-center justify-center transition-all border border-purple-200 shadow-sm hover:shadow"
                        title="Preview preference card"
                        data-testid={`preview-event-${event.event_id}`}
                      >
                        <Eye className="w-4 h-4 text-purple-600" />
                      </button>
                      )}
                      {/* Iter 9.40 Pass C - Host Notes (Pre/During/Post) trigger.
                          Only when user has RSVP'd AND host_user_id is known. */}
                      {event.isAttending && event.host_user_id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setHostNoteEvent(event);
                          }}
                          className="w-9 h-9 rounded-full bg-amber-50 hover:bg-amber-100 flex items-center justify-center transition-all border border-amber-200 shadow-sm hover:shadow"
                          title="Notes from your host"
                          data-testid={`host-note-trigger-${event.event_id}`}
                        >
                          <span className="text-base">📋</span>
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // If user has RSVP'd, go to confirmation page. Otherwise go to welcome.
                          const url = event.isAttending 
                            ? `/e/${event.slug || event.event_id}?pass=true`
                            : `/e/${event.slug || event.event_id}`;
                          navigate(url);
                        }}
                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                        title={event.isAttending ? "View your pass" : "View event page"}
                      >
                        <Link2 className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Experiences */}
              {experiencesWithDates.slice(0, 2).map((exp) => {
                const catConfig = CATEGORIES[exp.category] || CATEGORIES.nails;
                const CatIcon = catConfig.icon;
                return (
                  <Card 
                    key={exp.experience_id}
                    className="rounded-xl border-gray-100 shadow-sm hover:shadow-md transition-all"
                    data-testid={`exp-card-${exp.experience_id}`}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div 
                        className={`w-11 h-11 rounded-lg ${catConfig.color} flex items-center justify-center flex-shrink-0 cursor-pointer`}
                        onClick={() => openEditModal(exp)}
                      >
                        <CatIcon className="w-5 h-5" />
                      </div>
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => openEditModal(exp)}
                      >
                        <div className="flex items-center gap-1.5">
                          <h4 className="font-medium text-sm truncate">{exp.name}</h4>
                          <Badge className={`text-[9px] ${catConfig.color} px-1`}>{catConfig.name}</Badge>
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(exp.target_date).toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})}
                        </p>
                      </div>
                      {/* Quick link - if linked to event, go to event confirmation */}
                      <div className="flex items-center gap-1">
                        {/* Move to occasion (replacement for drag-drop) */}
                        <select
                          value={exp.linked_event_id || ""}
                          onClick={(e) => e.stopPropagation()}
                          onChange={async (e) => {
                            e.stopPropagation();
                            const newOcc = e.target.value || null;
                            try {
                              const token = localStorage.getItem("session_token");
                              const r = await fetch(`${API}/api/user/experiences/${exp.experience_id}`, {
                                method: "PATCH",
                                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                                body: JSON.stringify({ linked_event_id: newOcc })
                              });
                              if (r.ok) {
                                toast.success(newOcc ? "Linked to occasion" : "Unlinked");
                                revalidateAll?.();
                              }
                            } catch { /* silent */ }
                          }}
                          className="text-[10px] bg-gray-50 border border-gray-200 rounded px-1.5 py-1 max-w-[100px] truncate"
                          data-testid={`move-to-occasion-${exp.experience_id}`}
                          title="Move this experience to an occasion"
                        >
                          <option value="">No occasion</option>
                          {experiences.filter(e2 => e2.type === "personal_event").map(occ => (
                            <option key={occ.experience_id} value={occ.experience_id}>{occ.name}</option>
                          ))}
                        </select>
                        {/* Preview Eye for preference card */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewingExperience(exp);
                          }}
                          className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                          title="Preview preference card"
                          data-testid={`preview-exp-${exp.experience_id}`}
                        >
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        {exp.linked_event_id ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const linkedEvent = hostedEvents.find(ev => ev.event_id === exp.linked_event_id) || attendingEvents.find(ev => ev.event_id === exp.linked_event_id);
                              if (linkedEvent) navigate(`/e/${linkedEvent.slug || linkedEvent.event_id}`);
                            }}
                            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                            title="View linked event"
                          >
                            <Link2 className="w-4 h-4 text-gray-600" />
                          </button>
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Pending host applications waiting card - gated */}
      {hostingSectionVisible && <PendingHostApplicationsCard userEmail={user?.email} />}

      {/* Hosting Section - Hosts and Experiences - gated for bare-bones launch */}
      {hostingSectionVisible && (
      <HostingSection 
        hostedEvents={hostedEvents}
        organizations={organizations}
        onManageEvent={(event) => {
          navigate(`/host/v2/${event.event_id}`);
        }}
        onManageOrg={(org) => setMobileTab("settings")}
        onDeleteEvent={async (eventId) => {
          try {
            const token = localStorage.getItem("session_token");
            const res = await fetch(`${API}/api/admin/events/${eventId}`, {
              method: "DELETE",
              headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
              toast.success("Event deleted");
              revalidateAll();
            } else {
              const err = await res.json();
              toast.error(err.detail || "Failed to delete");
            }
          } catch { toast.error("Failed to delete event"); }
        }}
      />
      )}

      {/* Super Admin Pilots Section - Separate from personal hosting */}
      {(user?.is_super_admin === true || user?.role === 'super_admin' || user?.role === 'admin' || (user?.email && isSuperAdminEmail(user.email))) && (
        <SuperAdminPilotsSection navigate={navigate} />
      )}

      {/* Provider Dashboard removed - replaced by events/experiences under org names */}
    </div>
    );
  };
  // Mobile Defaults Tab - Clean implementation using new DefaultsTab component
  const MobileDefaultsTab = () => {
    // Determine user's relevant pilot categories from multiple sources:
    // 1. pilot_intakes they've completed
    // 2. RSVPs to events (determines event/instructor pilots)
    // 3. Tags on their profile (e.g., pilot_hair, pilot_events)
    // 4. Organizations they're part of
    
    const userPilotCategories = useMemo(() => {
      const categories = new Set();
      
      // From tags (e.g., "pilot_hair" -> "hair")
      (user?.tags || []).forEach(tag => {
        if (tag.startsWith('pilot_')) {
          categories.add(tag.replace('pilot_', '').toLowerCase());
        }
      });
      
      // From attending events (RSVPs) - if they have event RSVPs, they need event defaults
      if (attendingEvents && attendingEvents.length > 0) {
        categories.add('events');
        // Check event categories
        attendingEvents.forEach(event => {
          if (event.category) categories.add(event.category.toLowerCase());
          if (event.pilot_type) categories.add(event.pilot_type.toLowerCase());
          if (event.event_type) categories.add(event.event_type.toLowerCase());
        });
      }
      
      // From pilot intakes
      (user?.pilot_intakes || []).forEach(intake => {
        if (intake.pilot_type) categories.add(intake.pilot_type.toLowerCase());
        if (intake.category) categories.add(intake.category.toLowerCase());
      });
      
      // From organizations' pilots
      organizations?.forEach(org => {
        (org.pilots || []).forEach(pilot => {
          if (pilot.category) categories.add(pilot.category.toLowerCase());
          if (pilot.pilot_type) categories.add(pilot.pilot_type.toLowerCase());
          if (pilot.industry) categories.add(pilot.industry.toLowerCase());
        });
      });
      
      return Array.from(categories);
    }, [user, attendingEvents, organizations]);
    
    // Convert to format expected by DefaultsTab
    const userPilots = userPilotCategories.map(cat => ({
      category: cat,
      pilot_type: cat,
      industry: cat
    }));
    
    // Handler to save category data
    const handleSaveCategory = async (categoryId, data) => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/user/preferences/${categoryId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(data)
        });
        
        if (!res.ok) {
          throw new Error("Failed to save defaults");
        }
        
        // Update local state immediately for responsive UI
        setUserDefaults(prev => ({
          ...prev,
          defaults: { ...prev?.defaults, [categoryId]: data },
          filled_categories: [...new Set([...(prev?.filled_categories || []), categoryId])]
        }));
        
        // Revalidate SWR cache to ensure data consistency
        await revalidateAllUserData();
        
        toast.success(`${categoryId.charAt(0).toUpperCase() + categoryId.slice(1)} preferences saved!`);
      } catch (err) {
        console.error("Save error:", err);
        toast.error("Failed to save defaults");
      }
    };
    
    // Handler to clear category data
    const handleClearCategory = async (categoryId) => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/user/preferences/${categoryId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!res.ok) {
          throw new Error("Failed to clear defaults");
        }
        
        // Update local state immediately for responsive UI
        setUserDefaults(prev => ({
          ...prev,
          defaults: { ...prev?.defaults, [categoryId]: null },
          filled_categories: (prev?.filled_categories || []).filter(c => c !== categoryId)
        }));
        
        // Revalidate SWR cache to ensure data consistency
        await revalidateAllUserData();
        
        toast.success(`${categoryId.charAt(0).toUpperCase() + categoryId.slice(1)} defaults cleared`);
      } catch (err) {
        console.error("Clear error:", err);
        toast.error("Failed to clear defaults");
      }
    };
    
    return (
      <TabErrorBoundary>
        {/* Iter 9.40 Pass 4c - UniversalAllergensBlock removed.
            Universal Defaults below is now the single source of truth for
            allergies / accessibility / dietary across all experiences. */}
        <DefaultsTab 
          userDefaults={userDefaults}
          onSaveCategory={handleSaveCategory}
          onClearCategory={handleClearCategory}
          userPilots={userPilots}
        />
      </TabErrorBoundary>
    );
  };

  // Experience occasion types
  const OCCASION_TYPES = [
    { id: "datenight", label: "Date Night", icon: Heart, color: "bg-rose-100 text-rose-600" },
    { id: "wedding", label: "Wedding", icon: Heart, color: "bg-pink-100 text-pink-600" },
    { id: "workevent", label: "Work Event", icon: Folder, color: "bg-blue-100 text-blue-600" },
    { id: "nightout", label: "Night Out", icon: Sparkles, color: "bg-purple-100 text-purple-600" },
    { id: "photoshoot", label: "Photoshoot", icon: Camera, color: "bg-amber-100 text-amber-600" },
    { id: "maintenance", label: "Maintenance", icon: CalendarIcon, color: "bg-gray-100 text-gray-600" },
  ];

  // Mobile Inspo Tab
  const MobileInspoTab = () => {
    // Iter 9.47.l - Inspiration tab copy from Airtable.
    const { slotMap: ins } = useUiSurface('surface_me_inspo');
    // Group inspirations by category
    const inspoByCategory = inspirations.reduce((acc, inspo) => {
      const cat = inspo.category || 'uncategorized';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(inspo);
      return acc;
    }, {});
    
    const filteredInspo = activeInspoCategory === "all" 
      ? inspirations 
      : inspoByCategory[activeInspoCategory] || [];
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="me-inspo-title">{meTemplate?.inspo_title || slotText(ins, 'tab_title', 'Inspiration')}</h2>
          <Button size="sm" onClick={() => setShowAddInspoModal(true)} className="bg-black hover:bg-gray-800 rounded-full" data-testid="me-inspo-add-btn">
            <Plus className="w-4 h-4 mr-1" />{slotText(ins, 'add_btn_label', 'Add')}
          </Button>
        </div>

        {/* Iter 9.40 Pass C - Phygital / private / host-curated folders surface */}
        <InspoFoldersGrid
          categoryId={activeInspoCategory === "all" ? null : activeInspoCategory}
          onOpenFolder={(f) => {
            // Folder click → filter the legacy grid by the folder's category if one is set
            if (f.category_id) setActiveInspoCategory(f.category_id);
          }}
        />
        
        {/* Category Folders/Tabs - Wrap on desktop, scroll on mobile */}
        <div 
          className="flex gap-2 flex-wrap md:flex-wrap overflow-x-auto md:overflow-visible pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" 
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <button
            onClick={() => setActiveInspoCategory("all")}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              activeInspoCategory === "all" 
                ? "bg-black text-white" 
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            data-testid="inspo-tab-all"
          >
            All ({inspirations.length})
          </button>
          {/* Inspo categories - exclude audience (it's for events, not visual inspo). Icons + labels pulled from canonical categoryConfig.js single source of truth. */}
          {BEAUTY_CATEGORIES.map((catId) => {
            const cat = CATEGORIES[catId];
            const preset = getCategoryPreset(catId);
            const count = (inspoByCategory[catId] || []).length;
            if (!cat && !preset) return null;
            const CatIcon = (preset && preset.icon) || (cat && cat.icon);
            const labelText = (preset && (preset.inspoLabel || preset.name)) || (cat && cat.name) || catId;
            return (
              <button
                key={catId}
                onClick={() => setActiveInspoCategory(catId)}
                data-testid={`inspo-tab-${catId}`}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeInspoCategory === catId 
                    ? "bg-black text-white" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                style={activeInspoCategory === catId && preset ? { backgroundColor: preset.iconColor, color: "#fff" } : {}}
              >
                {CatIcon && <CatIcon className="w-4 h-4" />}
                {labelText} ({count})
              </button>
            );
          })}
        </div>
        
        {/* Subfolders for active category */}
        {activeInspoCategory !== "all" && (
          <div className="space-y-2" data-testid="inspo-subfolders">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Folders</p>
              <div className="flex items-center gap-1">
                <input
                  value={newSubfolderName}
                  onChange={(e) => setNewSubfolderName(e.target.value)}
                  placeholder="New folder..."
                  className="text-xs border rounded-lg px-2 py-1 w-28 focus:outline-none focus:ring-1 focus:ring-black"
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && newSubfolderName.trim()) {
                      const cat = activeInspoCategory;
                      const updated = { ...inspoSubfolders };
                      if (!updated[cat]) updated[cat] = [];
                      updated[cat] = [...updated[cat], newSubfolderName.trim()];
                      setInspoSubfolders(updated);
                      setNewSubfolderName("");
                      const token = localStorage.getItem("session_token");
                      await fetch(`${API}/me/inspo-subfolders`, {
                        method: "PUT",
                        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                        body: JSON.stringify({ folders: updated })
                      });
                    }
                  }}
                  data-testid="add-subfolder-input"
                />
                <button
                  onClick={async () => {
                    if (!newSubfolderName.trim()) return;
                    const cat = activeInspoCategory;
                    const updated = { ...inspoSubfolders };
                    if (!updated[cat]) updated[cat] = [];
                    updated[cat] = [...updated[cat], newSubfolderName.trim()];
                    setInspoSubfolders(updated);
                    setNewSubfolderName("");
                    const token = localStorage.getItem("session_token");
                    await fetch(`${API}/me/inspo-subfolders`, {
                      method: "PUT",
                      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ folders: updated })
                    });
                  }}
                  className="text-xs bg-black text-white rounded-lg px-2 py-1 hover:bg-gray-800"
                  data-testid="add-subfolder-btn"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setActiveSubfolder(null)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  !activeSubfolder ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              {(inspoSubfolders[activeInspoCategory] || []).map((folder, idx) => (
                <div key={folder + idx} className="flex items-center gap-0.5 flex-shrink-0 group">
                  {editingSubfolder === folder ? (
                    <input
                      autoFocus
                      defaultValue={folder}
                      className="text-xs border rounded-lg px-2 py-1.5 w-24"
                      onBlur={async (e) => {
                        const newName = e.target.value.trim();
                        if (newName && newName !== folder) {
                          const cat = activeInspoCategory;
                          const updated = { ...inspoSubfolders };
                          updated[cat] = updated[cat].map(f => f === folder ? newName : f);
                          setInspoSubfolders(updated);
                          if (activeSubfolder === folder) setActiveSubfolder(newName);
                          const token = localStorage.getItem("session_token");
                          await fetch(`${API}/me/inspo-subfolders`, {
                            method: "PUT",
                            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                            body: JSON.stringify({ folders: updated })
                          });
                        }
                        setEditingSubfolder(null);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                  ) : (
                    <button
                      onClick={() => setActiveSubfolder(activeSubfolder === folder ? null : folder)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        activeSubfolder === folder ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid={`subfolder-${folder}`}
                    >
                      {folder}
                    </button>
                  )}
                  {editingSubfolder !== folder && (
                    <>
                      <button
                        onClick={() => setEditingSubfolder(folder)}
                        className="text-gray-300 hover:text-blue-500 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`edit-subfolder-${folder}`}
                        title="Rename folder"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ type: "subfolder", folder, category: activeInspoCategory })}
                        className="text-gray-300 hover:text-red-500 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-subfolder-${folder}`}
                        title="Delete folder"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Inspiration Yet - Empty State (only when no inspo) */}
        {inspirations.length === 0 && (
          <div className="p-6 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-100 text-center" data-testid="me-inspo-empty-state">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white shadow-sm flex items-center justify-center">
              <Camera className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-testid="me-inspo-empty-title">{slotText(ins, 'empty_title', 'No Inspiration Yet')}</h3>
            <p className="text-sm text-gray-600 mb-4" data-testid="me-inspo-empty-body">{slotText(ins, 'empty_body', 'Save looks you love')}</p>
            
            <Button 
              onClick={() => setShowAddInspoModal(true)}
              className="bg-black hover:bg-gray-800"
              data-testid="me-inspo-empty-cta"
            >
              <ArrowUpFromLine className="w-4 h-4 mr-2" />
              {slotText(ins, 'empty_cta', 'Add Inspiration')}
            </Button>
          </div>
        )}
        
        {/* Selection Action Bar */}
        {selectedInspos.length > 0 && (
          <div className="sticky top-0 z-10 bg-purple-50 border border-purple-200 rounded-xl px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                <span className="text-xs font-bold text-white">{selectedInspos.length}</span>
              </div>
              <span className="text-sm text-gray-700">selected</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedInspos([])}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
              <X className="w-3 h-3 ml-1" />
            </Button>
          </div>
        )}
        
        
        {filteredInspo.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {filteredInspo.map((inspo) => {
              const cat = CATEGORIES[inspo.category] || CATEGORIES.nails;
              const isEditingNote = editingInspoNote === inspo.inspiration_id;
              const isInstagramEmbed = inspo.embed_type === 'instagram' || inspo.source === 'instagram_embed';
              
              // Instagram embeds get their own card style with built-in actions
              if (isInstagramEmbed) {
                return (
                  <div key={inspo.inspiration_id} className="col-span-2">
                    <InstagramEmbedCard 
                      postId={inspo.embed_id} 
                      url={inspo.image_url}
                      onCreateExperience={() => {
                        setSelectedInspos([inspo]);
                        startCreateExperience(inspo.category || 'nails');
                      }}
                      onDelete={() => deleteInspiration(inspo.inspiration_id)}
                    />
                  </div>
                );
              }
              
              // Regular image inspo cards
              const isFromDM = inspo.source === 'instagram_dm';
              const originator = inspo.originator;
              
              return (
                <Card 
                  key={inspo.inspiration_id} 
                  className={`rounded-2xl overflow-hidden border-2 shadow-sm group cursor-pointer transition-all ${
                    selectedInspos.some(s => s.inspiration_id === inspo.inspiration_id) 
                      ? 'border-purple-500 ring-2 ring-purple-200' 
                      : 'border-transparent hover:border-gray-200'
                  }`}
                  onClick={() => {
                    // Toggle selection
                    if (selectedInspos.some(s => s.inspiration_id === inspo.inspiration_id)) {
                      setSelectedInspos(selectedInspos.filter(s => s.inspiration_id !== inspo.inspiration_id));
                    } else {
                      setSelectedInspos([...selectedInspos, inspo]);
                    }
                  }}
                >
                  <div className="aspect-square relative">
                    <img 
                      src={getDisplayImageUrl(inspo.image_url)} 
                      alt="" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = PHYGITAL_AVATAR_BLACK;
                        e.target.className = "w-full h-full object-contain p-8 bg-gray-100";
                      }}
                    />
                    {/* Selection indicator */}
                    {selectedInspos.some(s => s.inspiration_id === inspo.inspiration_id) && (
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    {/* Category tag - clickable to edit */}
                    <div className="absolute bottom-2 left-2">
                      {editingInspoCategory === inspo.inspiration_id ? (
                        <div className="bg-white rounded-lg shadow-lg p-2 min-w-[120px]" onClick={(e) => e.stopPropagation()}>
                          <p className="text-[10px] text-gray-500 mb-1 font-medium">Change tag:</p>
                          <div className="flex flex-wrap gap-1">
                            {BEAUTY_CATEGORIES.map((catId) => {
                              const categoryConfig = CATEGORIES[catId];
                              if (!categoryConfig) return null;
                              return (
                                <button
                                  key={catId}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateInspoCategory(inspo.inspiration_id, catId);
                                  }}
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                                    inspo.category === catId 
                                      ? `${categoryConfig.color} ring-1 ring-offset-1` 
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {categoryConfig.name}
                                </button>
                              );
                            })}
                          </div>
                          <button 
                            onClick={(e) => { e.stopPropagation(); setEditingInspoCategory(null); }}
                            className="text-[10px] text-gray-400 hover:text-gray-600 mt-1.5 block w-full text-center"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingInspoCategory(inspo.inspiration_id); }}
                          className={`${cat.color} text-xs px-2 py-0.5 rounded-full hover:ring-2 hover:ring-offset-1 transition-all flex items-center gap-1`}
                          title="Click to change category"
                        >
                          {cat.name}
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {/* Action buttons - top right */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); setLightboxInspo(inspo); }}
                        className="w-7 h-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center hover:bg-white"
                        title="Expand Image"
                        data-testid={`expand-inspo-${inspo.inspiration_id}`}
                      >
                        <Maximize2 className="w-3.5 h-3.5 text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); createExperienceFromInspo(inspo); }}
                        className="w-7 h-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center hover:bg-white"
                        title="Create Experience"
                      >
                        <Wand2 className="w-3.5 h-3.5 text-purple-600" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeletingInspo(inspo); }}
                        className="w-7 h-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                    {/* Source/Originator tag - show original creator if available, or "via DM" */}
                    {isFromDM && (
                      inspo.original_creator ? (
                        // We have the original creator - link to their post
                        <a 
                          href={inspo.original_post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="absolute bottom-2 right-2 flex items-center gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] px-2 py-1 rounded-full hover:opacity-90"
                        >
                          <Instagram className="w-3 h-3" />
                          <span>@{inspo.original_creator}</span>
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ) : inspo.original_post_url ? (
                        // We have post URL but not creator name
                        <a 
                          href={inspo.original_post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="absolute bottom-2 right-2 flex items-center gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] px-2 py-1 rounded-full hover:opacity-90"
                        >
                          <Instagram className="w-3 h-3" />
                          <span>View Original</span>
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ) : (
                        // No original source - show "via DM"
                        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/60 text-white text-[10px] px-2 py-1 rounded-full">
                          <Instagram className="w-3 h-3" />
                          <span>via DM</span>
                        </div>
                      )
                    )}
                    {!isFromDM && inspo.source_platform && (
                      <Badge className="absolute top-2 right-2 bg-black/60 text-white text-[10px]">
                        {inspo.source_platform}
                      </Badge>
                    )}
                  </div>
                  {/* Note section */}
                  {(inspo.note || isEditingNote) && (
                    <div className="p-2 bg-gray-50">
                      {isEditingNote ? (
                        <div className="space-y-2">
                          <Textarea
                            value={inspoNoteText}
                            onChange={(e) => setInspoNoteText(e.target.value)}
                            placeholder="Add a note..."
                            className="text-xs resize-none h-16"
                          />
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              className="flex-1 h-7 text-xs"
                              onClick={() => saveInspoNote(inspo.inspiration_id, inspoNoteText)}
                            >
                              Save
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={() => {
                                setEditingInspoNote(null);
                                setInspoNoteText("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-600 line-clamp-2">{inspo.note}</p>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : inspirations.length === 0 ? null : (
          <Card className="rounded-2xl border-2 border-dashed border-gray-200">
            <CardContent className="py-8 text-center">
              <Camera className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <h3 className="font-medium mb-1 text-sm">
                {activeInspoCategory === "all" ? "No Inspiration Yet" : `No ${CATEGORIES[activeInspoCategory]?.name || ''} Inspiration`}
              </h3>
              <p className="text-xs text-gray-500 mb-3">Save looks you love</p>
              <Button onClick={() => setShowAddInspoModal(true)} size="sm" className="bg-black hover:bg-gray-800 rounded-full">
                <Plus className="w-4 h-4 mr-1" />Add Inspiration
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // MobileEventsCalendarTab REMOVED - Calendar is now integrated into ExperiencesTab

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="user-profile-page">
      <ViewAsBanner />
      {/* Iter 9.40 - banner after InterestPopup redirect (joined=host|waitlist|coming_soon|live) */}
      <div className="px-4 pt-3 space-y-3">
        <JoinedBanner />
        {/* Iter 9.40 Pass K - Top-level multi-dashboard switcher removed per
            user feedback (2026-05-07). Dashboards now only surface inside the
            Hosting section card (nested under the org). */}
      </div>
      {/* Mobile Header - Only notification, settings, user profile on right */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-gray-100 sticky top-0 z-40 md:hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-7 h-7" />
            <span className="font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>{companySettings.company_name?.toLowerCase() || "phygital"}</span>
          </Link>
          <div className="flex items-center gap-1">
            {/* Notifications Bell with Dropdown */}
            <NotificationBell />
            {/* Settings */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-9 w-9 p-0" 
              onClick={() => setMobileTab("settings")}
              data-testid="header-settings-btn"
            >
              <Settings className="w-5 h-5 text-gray-600" />
            </Button>
            {/* Profile Avatar  - clickable to /me */}
            <Link to="/me" className="flex-shrink-0 ml-1" data-testid="mobile-avatar-link">
              {user?.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt="" 
                  className="h-7 w-7 rounded-full object-cover"
                />
              ) : (
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">{user?.first_name?.charAt(0) || 'U'}</span>
                </div>
              )}
            </Link>
          </div>
        </div>
      </header>

      {/* Desktop Header - Only notification, settings, user profile on right */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 hidden md:block">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/me" className="flex items-center gap-2"><PhygitalIcon className="w-8 h-8" /><span className="font-semibold text-lg">{companySettings.company_name?.toLowerCase() || "phygital"}</span></Link>
          
          <div className="flex items-center gap-2">
            {/* Search button removed (Iter 9.47.u) per user feedback */}
            {/* Settings gear - always visible */}
            <Link to="/settings" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" title="Settings" data-testid="me-settings-gear">
              <Settings className="w-5 h-5 text-gray-500" />
            </Link>
            {/* Notifications Bell */}
            <NotificationBell />
            {/* Profile Avatar with Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex-shrink-0 flex items-center gap-1" data-testid="topbar-avatar-link">
                  {user?.profile_image ? (
                    <img src={user.profile_image} alt={user?.first_name || ''} className="h-8 w-8 rounded-full object-cover ring-2 ring-transparent hover:ring-gray-300 transition-all" />
                  ) : (
                    <DefaultAvatar 
                      firstName={user?.first_name || user?.name?.split(' ')[0]} 
                      lastName={user?.last_name || user?.name?.split(' ')[1]} 
                      size={32}
                      className="ring-2 ring-transparent hover:ring-gray-300 transition-all"
                    />
                  )}
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.first_name} {user?.last_name}</span>
                    <span className="text-xs font-normal text-gray-500">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/me" className="cursor-pointer"><User className="h-4 w-4 mr-2" /> My Profile</Link>
                </DropdownMenuItem>
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer"><Shield className="h-4 w-4 mr-2" /> Admin Panel</Link>
                    </DropdownMenuItem>
                    {(user?.is_super_admin === true || user?.role === 'super_admin' || (user?.email && isSuperAdminEmail(user.email))) && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin/settings" className="cursor-pointer" data-testid="admin-settings-link"><Key className="h-4 w-4 mr-2" /> Admin Settings</Link>
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Content Area */}
      <main className="md:hidden px-4 py-4 pb-24">
        {waitlistBanner && (
          <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 p-3 flex items-start gap-2" data-testid="waitlist-banner-mobile">
            <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-purple-900">You're on the waitlist!</p>
              <p className="text-xs text-purple-700">
                {waitlistBanner.category ? `We'll notify you when ${waitlistBanner.category} hosting opens in your city.` : "We'll notify you when hosting opens in your city."} Set up your profile while you wait.
              </p>
            </div>
            <button onClick={() => { setWaitlistBanner(null); window.history.replaceState({}, "", "/me"); }} className="text-purple-400 hover:text-purple-600" data-testid="waitlist-banner-mobile-close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {mobileTab === "home" && <MobileHomeTab />}
        {mobileTab === "experiences" && <ExperiencesPage embedded />}
        {mobileTab === "defaults" && <MobileDefaultsTab />}
        {mobileTab === "inspo" && <MobileInspoTab />}
        {mobileTab === "settings" && (
          <div className="space-y-4">
            <SettingsTab user={user} onUserUpdate={handleUserUpdate} meTemplate={meTemplate} />
            
            {/* Additional Settings Actions */}
            <Card className="rounded-xl border-gray-200">
              <CardContent className="p-4 space-y-2">
                {/* Quick Access Portals - dynamically loaded */}
                <QuickAccessPortals user={user} navigate={navigate} API={API} />
                
                {/* Timezone - Editable */}
                <div className="w-full p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-600" />
                    <div className="flex-1">
                      <span className="font-medium text-sm">Timezone</span>
                      {editingTimezone ? (
                        <select
                          value={userTimezone}
                          onChange={(e) => saveTimezone(e.target.value)}
                          className="mt-1 w-full text-sm border rounded-lg p-2 bg-white"
                          data-testid="timezone-select"
                        >
                          {TIMEZONE_OPTIONS.map(tz => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                          ))}
                          {!TIMEZONE_OPTIONS.find(t => t.value === userTimezone) && (
                            <option value={userTimezone}>{userTimezone}</option>
                          )}
                        </select>
                      ) : (
                        <p className="text-xs text-gray-500">
                          {TIMEZONE_OPTIONS.find(t => t.value === userTimezone)?.label || userTimezone}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setEditingTimezone(!editingTimezone)}
                      className="text-xs text-violet-600 hover:underline"
                    >
                      {editingTimezone ? "Cancel" : "Edit"}
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Logout & Delete Account */}
            <div className="space-y-2">
              <LogoutButton 
                logout={logout} 
                onLogout={() => navigate("/")} 
                variant="outline"
                showIcon={true}
                showText={true}
                className="w-full justify-start gap-3 p-4 h-auto bg-white rounded-xl border border-red-100 text-red-600 hover:text-red-700 hover:bg-red-50"
              />
              
              <button 
                onClick={() => setShowDeleteAccountModal(true)}
                className="w-full flex items-center gap-3 p-4 bg-white rounded-xl border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-5 h-5" />
                <div className="flex-1 text-left">
                  <span className="font-medium text-sm">Delete Account</span>
                  <p className="text-xs text-red-400">Permanently remove all your data</p>
                </div>
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Desktop Content - Same as mobile, just larger layout */}
      <main className="hidden md:block max-w-2xl mx-auto px-6 py-6">
        {/* Desktop Tab Navigation */}
        <div className="flex items-center justify-center mb-6 bg-gray-100 p-1 rounded-2xl">
          {[
            { id: "home", label: meTemplate?.dashboard_title || "Dashboard", icon: Home },
            { id: "defaults", label: meTemplate?.defaults_title || "Preferences", icon: Heart },
            { id: "inspo", label: meTemplate?.inspo_title || "Inspo", icon: Camera },
            { id: "experiences", label: meTemplate?.experience_title || "Experiences", icon: Sparkles },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setMobileTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                  mobileTab === tab.id
                    ? "bg-white shadow-sm font-medium"
                    : "text-gray-600 hover:text-black"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Desktop content - same components as mobile */}
        <div className="space-y-6">
          {waitlistBanner && (
            <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 flex items-start gap-3" data-testid="waitlist-banner-desktop">
              <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-purple-900">You're on the waitlist!</p>
                <p className="text-xs text-purple-700 mt-0.5">
                  {waitlistBanner.category ? `We'll notify you when ${waitlistBanner.category} hosting opens in your city.` : "We'll notify you when hosting opens in your city."} Set up your profile while you wait.
                </p>
              </div>
              <button onClick={() => { setWaitlistBanner(null); window.history.replaceState({}, "", "/me"); }} className="text-purple-400 hover:text-purple-600" data-testid="waitlist-banner-desktop-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {mobileTab === "home" && <MobileHomeTab />}
          {mobileTab === "experiences" && <ExperiencesPage embedded />}
          {mobileTab === "defaults" && <MobileDefaultsTab />}
          {mobileTab === "inspo" && <MobileInspoTab />}
          {mobileTab === "settings" && (
            <div className="space-y-4">
              <SettingsTab user={user} onUserUpdate={handleUserUpdate} meTemplate={meTemplate} />
              
              {/* Additional Settings Actions */}
              <Card className="rounded-xl border-gray-200">
                <CardContent className="p-4 space-y-2">
                  {/* Quick Access Portals - dynamically loaded */}
                  <QuickAccessPortals user={user} navigate={navigate} API={API} />
                  
                  {/* Timezone - Editable */}
                  <div className="w-full p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-gray-600" />
                      <div className="flex-1">
                        <span className="font-medium text-sm">Timezone</span>
                        {editingTimezone ? (
                          <select
                            value={userTimezone}
                            onChange={(e) => saveTimezone(e.target.value)}
                            className="mt-1 w-full text-sm border rounded-lg p-2 bg-white"
                            data-testid="desktop-timezone-select"
                          >
                            {TIMEZONE_OPTIONS.map(tz => (
                              <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                            {!TIMEZONE_OPTIONS.find(t => t.value === userTimezone) && (
                              <option value={userTimezone}>{userTimezone}</option>
                            )}
                          </select>
                        ) : (
                          <p className="text-xs text-gray-500">
                            {TIMEZONE_OPTIONS.find(t => t.value === userTimezone)?.label || userTimezone}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => setEditingTimezone(!editingTimezone)}
                        className="text-xs text-violet-600 hover:underline"
                      >
                        {editingTimezone ? "Cancel" : "Edit"}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Logout & Delete Account */}
              <div className="space-y-2">
                <LogoutButton 
                  logout={logout} 
                  onLogout={() => navigate("/")} 
                  variant="outline"
                  showIcon={true}
                  showText={true}
                  className="w-full justify-start gap-3 p-4 h-auto bg-white rounded-xl border border-red-100 text-red-600 hover:text-red-700 hover:bg-red-50"
                />
                
                <button 
                  onClick={() => setShowDeleteAccountModal(true)}
                  className="w-full flex items-center gap-3 p-4 bg-white rounded-xl border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-5 h-5" />
                  <div className="flex-1 text-left">
                    <span className="font-medium text-sm">Delete Account</span>
                    <p className="text-xs text-red-400">Permanently remove all your data</p>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Bottom Navigation - Mobile Only */}
      {/* Pass 4g - bottom navigation bar removed per user request */}

      {/* ============ CREATE EXPERIENCE MODAL (Step-based) ============ */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) { setSelectedCategory(null); setSelectedDate(null); setCreateStep(1); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-pink-500" />
              Create Experience
              <span className="ml-auto text-xs text-gray-400 font-normal">Step {createStep} of 3</span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Progress Bar */}
          <div className="flex gap-1.5 mb-4">
            {[1, 2, 3].map((step) => (
              <div 
                key={step} 
                className={`h-1.5 rounded-full flex-1 transition-all ${
                  step === createStep ? 'bg-pink-500' : step < createStep ? 'bg-pink-200' : 'bg-gray-200'
                }`} 
              />
            ))}
          </div>
          
          {/* Step 1: Category & Occasion */}
          {createStep === 1 && (
            <div className="space-y-5 py-2">
              {/* Category Selection */}
              <div>
                <label className="text-sm font-medium mb-3 block">What type of service?</label>
                <div className="grid grid-cols-3 gap-2">
                  {PILOT_CATEGORIES.map((catId) => {
                    const catConfig = CATEGORIES[catId];
                    if (!catConfig) return null;
                    const CategoryIcon = catConfig.icon;
                    return (
                      <button 
                        key={catId} 
                        onClick={() => setNewExperience({...newExperience, category: catId})} 
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-all ${
                          newExperience.category === catId 
                            ? `border-pink-500 bg-gradient-to-br ${catConfig.lightGradient}` 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid={`category-select-${catId}`}
                      >
                        <CategoryIcon className={`w-6 h-6 ${newExperience.category === catId ? "text-pink-600" : "text-gray-500"}`} />
                        <span className={`text-xs ${newExperience.category === catId ? "font-medium" : ""}`}>{catConfig.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Occasion Selection */}
              <div>
                <label className="text-sm font-medium mb-3 block">What&apos;s the occasion?</label>
                <div className="grid grid-cols-2 gap-2">
                  {OCCASION_OPTIONS.map((occ) => (
                    <button
                      key={occ.id}
                      onClick={() => setNewExperience({...newExperience, occasion: occ.id})}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 text-left transition-all ${
                        newExperience.occasion === occ.id 
                          ? "border-pink-500 bg-pink-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <span className="text-lg">{occ.icon}</span>
                      <span className={`text-sm ${newExperience.occasion === occ.id ? "font-medium" : ""}`}>{occ.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Next Button */}
              <div className="pt-4">
                <Button 
                  onClick={() => setCreateStep(2)} 
                  disabled={!newExperience.category}
                  className="w-full bg-black hover:bg-gray-800"
                >
                  Continue <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 2: Defaults & Inspo (Side by Side) */}
          {createStep === 2 && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                {/* Left: Defaults Preview */}
                <div className="space-y-3">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Palette className="w-4 h-4 text-blue-500" />
                    Your Defaults
                  </label>
                  <div className="bg-gray-50 rounded-xl p-3 min-h-[140px]">
                    {(() => {
                      const catConfig = CATEGORIES[newExperience.category] || CATEGORIES.nails;
                      const catPref = preferences?.[newExperience.category] || {};
                      return (
                        <div className="space-y-2">
                          <div className={`p-2 rounded-lg ${catConfig.color}`}>
                            <span className="text-xs font-medium">{catConfig.name} Defaults</span>
                          </div>
                          {Object.keys(catPref).length > 0 ? (
                            <div className="text-xs text-gray-600 space-y-1">
                              {Object.entries(catPref).slice(0, 3).map(([key, val]) => (
                                <div key={key} className="flex justify-between">
                                  <span className="text-gray-400">{key}:</span>
                                  <span>{val}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 text-center py-3">
                              No defaults set
                              <button 
                                onClick={() => { setShowCreateModal(false); setMobileTab("defaults"); }}
                                className="block text-pink-500 hover:underline mt-1 mx-auto"
                              >
                                Set defaults
                              </button>
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Right: Inspo Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-pink-500" />
                    Inspo Photos
                    {selectedInspos.length > 0 && (
                      <Badge className="bg-pink-100 text-pink-700 text-[10px]">{selectedInspos.length}</Badge>
                    )}
                  </label>
                  <div className="bg-gray-50 rounded-xl p-2 min-h-[140px]">
                    {inspirations.length > 0 ? (
                      <div className="grid grid-cols-3 gap-1.5 max-h-[180px] overflow-y-auto">
                        {inspirations.map((insp) => {
                          const isSelected = selectedInspos.find(i => i.inspiration_id === insp.inspiration_id);
                          const matchesCategory = (insp.category || 'nails') === newExperience.category;
                          return (
                            <button 
                              key={insp.inspiration_id} 
                              onClick={() => toggleInspoSelection(insp)} 
                              className={`aspect-square rounded-lg overflow-hidden relative border-2 transition-all ${
                                isSelected 
                                  ? "border-pink-500 ring-1 ring-pink-200" 
                                  : matchesCategory 
                                    ? "border-pink-200" 
                                    : "border-transparent"
                              }`}
                            >
                              <img 
                                src={getDisplayImageUrl(insp.image_url)} 
                                alt="" 
                                className="w-full h-full object-cover" 
                                onError={(e) => { e.target.src = PHYGITAL_AVATAR_BLACK; e.target.className = "w-full h-full object-contain p-1 bg-gray-100"; }}
                              />
                              {isSelected && (
                                <div className="absolute inset-0 bg-pink-500/40 flex items-center justify-center">
                                  <Check className="w-4 h-4 text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full py-4">
                        <ImageIcon className="w-6 h-6 text-gray-300 mb-1" />
                        <p className="text-xs text-gray-400 text-center">
                          No inspo yet
                          <button 
                            onClick={() => { setShowCreateModal(false); setShowAddInspoModal(true); }}
                            className="block text-pink-500 hover:underline mt-1"
                          >
                            Add inspo
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Navigation */}
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setCreateStep(1)} className="flex-1">
                  <ChevronRight className="w-4 h-4 mr-2 rotate-180" /> Back
                </Button>
                <Button onClick={() => setCreateStep(3)} className="flex-1 bg-black hover:bg-gray-800">
                  Continue <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 3: Details */}
          {createStep === 3 && (
            <div className="space-y-4 py-2">
              {/* Name */}
              <div>
                <label className="text-sm font-medium mb-2 block">Name your experience</label>
                <Input 
                  placeholder={`e.g., ${newExperience.occasion === 'wedding' ? 'Wedding Day Look' : newExperience.occasion === 'birthday' ? 'Birthday Glam' : 'My Next Appointment'}`}
                  value={newExperience.name} 
                  onChange={(e) => setNewExperience({...newExperience, name: e.target.value})} 
                  data-testid="experience-name-input" 
                />
              </div>
              
              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                    <CalendarIcon className="w-3.5 h-3.5" /> Date
                  </label>
                  <Input 
                    type="date" 
                    value={newExperience.target_date} 
                    onChange={(e) => setNewExperience({...newExperience, target_date: e.target.value, no_date_yet: false})}
                    disabled={newExperience.no_date_yet}
                    data-testid="experience-date-input" 
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> Time
                  </label>
                  <Input 
                    type="time" 
                    value={newExperience.appointment_time || ""} 
                    onChange={(e) => setNewExperience({...newExperience, appointment_time: e.target.value})}
                    disabled={newExperience.no_date_yet}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={newExperience.no_date_yet || false}
                  onChange={(e) => setNewExperience({...newExperience, no_date_yet: e.target.checked, target_date: '', appointment_time: ''})}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-600">Don&apos;t know the date yet</span>
              </label>
              
              {/* Note */}
              <div>
                <label className="text-sm font-medium mb-2 block">Note for stylist (optional)</label>
                <Textarea 
                  placeholder="Any special requests or things they should know..." 
                  value={newExperience.stylist_note} 
                  onChange={(e) => setNewExperience({...newExperience, stylist_note: e.target.value})} 
                  rows={2}
                  data-testid="experience-note-input" 
                />
              </div>
              
              {/* Summary */}
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4">
                <h4 className="text-sm font-medium mb-2">Summary</h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Category:</span>
                    <span className="font-medium">{CATEGORIES[newExperience.category]?.name || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Occasion:</span>
                    <span className="font-medium">{OCCASION_OPTIONS.find(o => o.id === newExperience.occasion)?.label || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Inspo:</span>
                    <span className="font-medium">{selectedInspos.length} photos</span>
                  </div>
                </div>
              </div>
              
              {/* Navigation */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setCreateStep(2)} className="flex-1">
                  <ChevronRight className="w-4 h-4 mr-2 rotate-180" /> Back
                </Button>
                <Button 
                  onClick={createExperience} 
                  disabled={creatingExperience || !newExperience.name} 
                  className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600" 
                  data-testid="create-experience-btn"
                >
                  {creatingExperience ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" />Create</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ EXPERIENCE PREFERENCE CARD PREVIEW (read-only, eye icon) ============ */}
      <Dialog open={!!previewingExperience} onOpenChange={(o) => !o && setPreviewingExperience(null)}>
        <DialogContent className="max-w-md p-0 overflow-hidden bg-transparent border-0 shadow-none">
          {previewingExperience && (() => {
            const preset = getCategoryPreset?.(previewingExperience.category) || { iconColor: "#9c57f5", bgColor: "#f3f4f6", name: previewingExperience.category };
            const PresetIcon = preset.icon || Sparkles;
            const inspoUrls = (previewingExperience.inspiration_ids || []).map(id => {
              const insp = inspirations.find(i => i.inspiration_id === id);
              return insp?.image_url;
            }).filter(Boolean);
            const dateStr = previewingExperience.target_date
              ? new Date(previewingExperience.target_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
              : "Date TBD";
            return (
              <div className="rounded-3xl overflow-hidden shadow-2xl bg-white" data-testid="exp-preference-preview-card">
                {/* Hero */}
                <div className="px-6 pt-6 pb-4 text-white" style={{ background: `linear-gradient(135deg, ${preset.iconColor} 0%, ${preset.iconColor}cc 100%)` }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                      <PresetIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider opacity-80">{preset.name || previewingExperience.category}</p>
                      <h3 className="text-lg font-bold truncate">{previewingExperience.name}</h3>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs opacity-90">
                    <span className="flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" />{dateStr}</span>
                    {previewingExperience.appointment_time && <span className="flex items-center gap-1">@ {previewingExperience.appointment_time}</span>}
                  </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Status</span>
                    <Badge variant="secondary" className="capitalize">{previewingExperience.status || "draft"}</Badge>
                  </div>

                  {/* Provider */}
                  {previewingExperience.provider_name ? (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Provider</span>
                      <span className="font-medium text-gray-900">{previewingExperience.provider_name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Provider</span>
                      <span className="text-amber-600 font-medium">Still deciding</span>
                    </div>
                  )}

                  {/* Notes */}
                  {previewingExperience.stylist_note && (
                    <div className="text-xs">
                      <p className="text-gray-500 mb-1">Note for provider</p>
                      <p className="text-gray-900 bg-gray-50 rounded-lg p-3 leading-relaxed">{previewingExperience.stylist_note}</p>
                    </div>
                  )}

                  {/* Linked inspiration grid */}
                  {inspoUrls.length > 0 ? (
                    <div className="text-xs">
                      <p className="text-gray-500 mb-2">Linked Inspiration ({inspoUrls.length})</p>
                      <div className="grid grid-cols-3 gap-2">
                        {inspoUrls.slice(0, 6).map((url, i) => (
                          <img key={i} src={url} alt="" className="w-full aspect-square rounded-lg object-cover" />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">No inspiration photos linked</div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="flex-1 h-9 text-xs" onClick={() => setPreviewingExperience(null)} data-testid="preview-close-btn">Close</Button>
                    <Button size="sm" className="flex-1 h-9 text-xs bg-black hover:bg-gray-800" onClick={() => { setPreviewingExperience(null); openEditModal(previewingExperience); }} data-testid="preview-see-more-btn">
                      See more details
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ============ EDIT EXPERIENCE MODAL ============ */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit3 className="w-5 h-5 text-blue-500" />Edit Experience</DialogTitle></DialogHeader>
          {editingExperience && (
            <div className="space-y-4 py-4">
              <div><label className="text-sm font-medium mb-2 block">Name</label><Input value={editingExperience.name} onChange={(e) => setEditingExperience({...editingExperience, name: e.target.value})} /></div>
              
              {/* Date and Time Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" /> Date
                  </label>
                  <Input type="date" value={editingExperience.target_date || ""} onChange={(e) => setEditingExperience({...editingExperience, target_date: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Time
                  </label>
                  <Input type="time" value={editingExperience.appointment_time || ""} onChange={(e) => setEditingExperience({...editingExperience, appointment_time: e.target.value})} />
                </div>
              </div>
              
              <div><label className="text-sm font-medium mb-2 block">Note for Stylist</label><Textarea value={editingExperience.stylist_note || ""} onChange={(e) => setEditingExperience({...editingExperience, stylist_note: e.target.value})} rows={3} /></div>
              
              {/* Link Inspo Images */}
              <div className="p-4 bg-pink-50/50 rounded-xl">
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-pink-500" />
                  Linked Inspiration
                  {(editingExperience.inspiration_ids || []).length > 0 && (
                    <Badge className="bg-pink-100 text-pink-700 text-[10px]">
                      {(editingExperience.inspiration_ids || []).length} linked
                    </Badge>
                  )}
                </label>
                
                {inspirations.length > 0 ? (
                  <div className="grid grid-cols-5 gap-2 max-h-32 overflow-y-auto">
                    {inspirations.map((insp) => {
                      const isLinked = (editingExperience.inspiration_ids || []).includes(insp.inspiration_id);
                      return (
                        <button 
                          key={insp.inspiration_id} 
                          onClick={() => {
                            const currentIds = editingExperience.inspiration_ids || [];
                            const newIds = isLinked 
                              ? currentIds.filter(id => id !== insp.inspiration_id)
                              : [...currentIds, insp.inspiration_id];
                            setEditingExperience({...editingExperience, inspiration_ids: newIds});
                          }} 
                          className={`aspect-square rounded-lg overflow-hidden relative border-2 transition-all ${
                            isLinked ? "border-pink-500 ring-2 ring-pink-200" : "border-gray-200"
                          }`}
                        >
                          <img 
                            src={getDisplayImageUrl(insp.image_url)} 
                            alt="" 
                            className="w-full h-full object-cover" 
                            onError={(e) => { e.target.src = PHYGITAL_AVATAR_BLACK; e.target.className = "w-full h-full object-contain p-1 bg-gray-100"; }}
                          />
                          {isLinked && (
                            <div className="absolute inset-0 bg-pink-500/30 flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No inspo photos available</p>
                )}
              </div>
              
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Status</span>
                  <Badge className={STATUS_CONFIG[editingExperience.status]?.color}>{STATUS_CONFIG[editingExperience.status]?.label}</Badge>
                </div>
              </div>
              
              {/* Provider Section */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-500" />
                    Provider
                  </span>
                  {editingExperience.provider_info?.name && (
                    <Badge variant="secondary" className="text-xs">{editingExperience.provider_info.name}</Badge>
                  )}
                </div>
                
                <Button variant="outline" size="sm" onClick={() => { setShowEditModal(false); openLinkProviderModal(editingExperience); }} className="w-full">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  {editingExperience.provider_info?.name ? "Update Provider" : "Link to Your Stylist"}
                </Button>
                
                {/* Provider Notification Options */}
                {editingExperience.provider_info?.name && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Notify your provider about changes:</p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 text-xs"
                        onClick={() => shareExperienceUpdate(editingExperience.experience_id)}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy Update Link
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 text-xs"
                        onClick={() => {
                          const phone = editingExperience.provider_info?.phone;
                          if (phone) {
                            window.open(`sms:${phone}?body=I've updated my experience preferences! Check it here: ${window.location.origin}/share/${editingExperience.share_token || ''}`);
                          } else {
                            toast.info("Add provider phone to send SMS");
                          }
                        }}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Send SMS
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={async () => {
                if (window.confirm("Are you sure you want to delete this experience?")) {
                  try {
                    const token = localStorage.getItem("session_token");
                    await fetch(`${API}/experiences/${editingExperience.experience_id}`, {
                      method: "DELETE",
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    // Revalidate via SWR instead of local state mutation
                    await revalidateAll();
                    setShowEditModal(false);
                    toast.success("Experience deleted");
                  } catch (err) {
                    toast.error("Failed to delete");
                  }
                }
              }} 
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">Cancel</Button>
            {editingExperience?.provider_info?.name ? (
              <>
                <Button variant="outline" onClick={() => updateExperience(false)} className="flex-1">Save</Button>
                <Button onClick={() => updateExperience(true)} className="flex-1">
                  <Bell className="w-4 h-4 mr-1" />
                  Save & Notify
                </Button>
              </>
            ) : (
              <Button onClick={() => updateExperience(false)} className="flex-1">Save Changes</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ LINK PROVIDER MODAL ============ */}
      <Dialog open={showLinkProviderModal} onOpenChange={setShowLinkProviderModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><User className="w-5 h-5 text-green-500" />Link Your Stylist</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">Add your stylist info to keep everything organized. You can also share this experience directly with them.</p>
            
            <div><label className="text-sm font-medium mb-1 block">Stylist Name</label><Input placeholder="Their name" value={providerInfo.name} onChange={(e) => setProviderInfo({...providerInfo, name: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Phone</label><Input type="tel" placeholder="555-123-4567" value={providerInfo.phone} onChange={(e) => setProviderInfo({...providerInfo, phone: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Email</label><Input type="email" placeholder="stylist@example.com" value={providerInfo.email} onChange={(e) => setProviderInfo({...providerInfo, email: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Website</label><Input placeholder="https://..." value={providerInfo.website} onChange={(e) => setProviderInfo({...providerInfo, website: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Instagram</label><Input placeholder="@handle" value={providerInfo.instagram} onChange={(e) => setProviderInfo({...providerInfo, instagram: e.target.value})} /></div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <p>If your stylist has a Phygital account, they can see your shared experience and confirm the booking!</p>
            </div>
          </div>
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowLinkProviderModal(false)} className="flex-1">Cancel</Button>
            <Button onClick={linkProvider} disabled={!providerInfo.name} className="flex-1">Link Stylist</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ DELETE INSPO CONFIRMATION ============ */}
      {/* ============ INSPO LIGHTBOX MODAL ============ */}
      <Dialog open={!!lightboxInspo} onOpenChange={(open) => !open && setLightboxInspo(null)}>
        <DialogContent className="max-w-2xl p-0 bg-black/95 border-none overflow-hidden" data-testid="inspo-lightbox">
          {lightboxInspo && (
            <div className="relative">
              <img 
                src={getDisplayImageUrl(lightboxInspo.image_url)} 
                alt={lightboxInspo.note || "Inspiration"} 
                className="w-full max-h-[80vh] object-contain"
              />
              <button
                onClick={() => setLightboxInspo(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                data-testid="close-lightbox"
              >
                <X className="w-5 h-5" />
              </button>
              {lightboxInspo.note && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-8">
                  <p className="text-white text-sm">{lightboxInspo.note}</p>
                </div>
              )}
              <div className="absolute bottom-3 right-3 flex gap-2">
                <button
                  onClick={() => { createExperienceFromInspo(lightboxInspo); setLightboxInspo(null); }}
                  className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium hover:bg-white/30 flex items-center gap-1"
                >
                  <Wand2 className="w-3 h-3" /> Create Experience
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ DELETE INSPO CONFIRMATION ============ */}
      <Dialog open={!!deletingInspo} onOpenChange={(open) => !open && setDeletingInspo(null)}>
        <DialogContent className="max-w-xs p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">Delete Inspiration?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {deletingInspo && (
              <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200">
                  <img 
                    src={getDisplayImageUrl(deletingInspo.image_url)} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium">{CATEGORIES[deletingInspo.category]?.name || 'Inspiration'}</p>
                  <p className="text-xs text-gray-500">This cannot be undone</p>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDeletingInspo(null)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={() => deleteInspiration(deletingInspo?.inspiration_id)} 
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ DELETE EXPERIENCE CONFIRMATION (Iter 9.47.q) ============ */}
      <ConfirmDeleteDialog
        open={!!deletingExperience}
        onOpenChange={(o) => !o && setDeletingExperience(null)}
        title="Delete this experience?"
        itemLabel={deletingExperience?.name || deletingExperience?.title}
        loading={deletingExpInFlight}
        onConfirm={() => deletingExperience && deleteExperience(deletingExperience.experience_id)}
      />

      {/* ============ ADD INSPO MODAL ============ */}
      <Dialog open={showAddInspoModal} onOpenChange={(open) => {
        setShowAddInspoModal(open);
        if (!open) {
          setInspoSource(null);
          setNewInspoUrl("");
        }
      }}>
        <DialogContent className="max-w-xs p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base flex items-center gap-2">
              <Camera className="w-4 h-4 text-pink-500" />Add Inspiration
            </DialogTitle>
          </DialogHeader>
          
          {/* Step 1: Select Category First */}
          {!inspoSource && (
            <div className="space-y-4">
              {/* Category Selection */}
              <div>
                <label className="text-xs font-medium mb-2 block text-gray-600">What category is this for?</label>
                <div className="flex flex-wrap gap-2">
                  {INSPO_CATEGORIES.map((catId) => {
                    const cat = CATEGORIES[catId];
                    if (!cat) return null;
                    return (
                      <button 
                        key={catId} 
                        onClick={() => setNewInspoCategory(catId)} 
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${newInspoCategory === catId ? `${cat.color} ring-2 ring-offset-1` : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        {cat.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Upload Only - NO URL PASTE per Platform Law #3 */}
              <div className="pt-2 border-t">
                <button 
                  onClick={() => fileInputRef.current?.click()} 
                  className="w-full p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-purple-400 transition-all flex items-center gap-3 bg-gradient-to-r from-purple-50 to-pink-50" 
                  data-testid="inspo-upload"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Upload className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="font-semibold text-sm">Upload Photo</h4>
                    <p className="text-xs text-gray-500">From your device - PNG, JPG, WEBP</p>
                  </div>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ PROVIDER SEARCH MODAL ============ */}
      <Dialog open={showProviderSearchModal} onOpenChange={setShowProviderSearchModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-purple-500" />
              Find a Provider
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">Search by username, name, or business to share your preferences.</p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search @username, name, or business..."
                value={providerSearchQuery}
                onChange={(e) => {
                  setProviderSearchQuery(e.target.value);
                  searchProviders(e.target.value);
                }}
                className="pl-10"
                data-testid="provider-search-input"
              />
            </div>
            
            {/* Search Results */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {searchingProviders && (
                <div className="text-center py-4">
                  <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              )}
              
              {!searchingProviders && providerSearchResults.length > 0 && (
                providerSearchResults.map((provider) => (
                  <div
                    key={provider.provider_id}
                    className="p-3 bg-gray-50 rounded-lg border hover:border-purple-300 transition-colors"
                    data-testid={`provider-result-${provider.provider_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold">
                          {provider.business_name?.charAt(0) || "P"}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{provider.business_name}</p>
                          {provider.username && (
                            <p className="text-xs text-gray-500">@{provider.username}</p>
                          )}
                          <div className="flex gap-1 mt-1">
                            {provider.service_types?.map((type, i) => (
                              <Badge key={i} variant="secondary" className="text-[10px]">{type}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => shareWalletWithProvider(provider)}
                        data-testid={`share-wallet-${provider.provider_id}`}
                      >
                        <Share className="w-3 h-3 mr-1" />
                        Share
                      </Button>
                    </div>
                  </div>
                ))
              )}
              
              {!searchingProviders && providerSearchQuery.length >= 2 && providerSearchResults.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No providers found</p>
                  <p className="text-xs">Try a different search term</p>
                </div>
              )}
              
              {providerSearchQuery.length < 2 && (
                <div className="text-center py-6 text-gray-400">
                  <p className="text-sm">Type at least 2 characters to search</p>
                </div>
              )}
            </div>
            
            <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-700">
              <p><strong>Tip:</strong> When you share your defaults, providers can see your saved preferences and inspiration to serve you better!</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden file input for profile photo */}
      <input
        type="file"
        ref={profilePhotoRef}
        onChange={handleProfilePhotoSelect}
        accept="image/*"
        className="hidden"
      />
      
      {/* Hidden file input for inspo uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleInspoFileUpload}
        accept="image/*"
        className="hidden"
      />

      {/* ============ EXPANDED PROFILE PHOTO MODAL ============ */}
      <Dialog open={showExpandedPhoto} onOpenChange={setShowExpandedPhoto}>
        <DialogContent className="max-w-lg p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4" /> Profile Photo
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <div className="w-64 h-64 rounded-xl overflow-hidden bg-gray-100">
              {user?.profile_photo_url ? (
                <img src={user.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-16 h-16 text-gray-300" />
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => profilePhotoRef.current?.click()}>
                <Camera className="w-4 h-4 mr-2" /> Change Photo
              </Button>
              {user?.profile_photo_url && (
                <Button variant="ghost" size="sm" onClick={() => setShowExpandedPhoto(false)}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ CROP PHOTO MODAL ============ */}
      <Dialog open={showCropModal} onOpenChange={(open) => {
        if (!open) {
          setSelectedPhotoFile(null);
          setCropPreview(null);
          setCrop({ x: 0, y: 0 });
          setZoom(1);
          setRotation(0);
        }
        setShowCropModal(open);
      }}>
        <DialogContent className="max-w-md p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base flex items-center gap-2">
              <Camera className="w-4 h-4" /> Crop Your Photo
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {/* Cropper Container */}
            <div className="relative w-full h-64 bg-gray-900 rounded-xl overflow-hidden">
              {cropPreview && (
                <Cropper
                  image={cropPreview}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              )}
            </div>
            
            {/* Zoom Control */}
            <div className="flex items-center gap-3">
              <ZoomIn className="w-4 h-4 text-gray-500" />
              <Slider
                value={[zoom]}
                onValueChange={(v) => setZoom(v[0])}
                min={1}
                max={3}
                step={0.1}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-10">{zoom.toFixed(1)}x</span>
            </div>
            
            {/* Rotation Control */}
            <div className="flex items-center gap-3">
              <RotateCw className="w-4 h-4 text-gray-500" />
              <Slider
                value={[rotation]}
                onValueChange={(v) => setRotation(v[0])}
                min={0}
                max={360}
                step={1}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-10">{rotation}°</span>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              Drag to position, pinch or use slider to zoom
            </p>
            
            <div className="flex gap-2 w-full">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => {
                  setShowCropModal(false);
                  setSelectedPhotoFile(null);
                  setCropPreview(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                size="sm" 
                className="flex-1"
                onClick={uploadProfilePhoto}
              >
                <Upload className="w-4 h-4 mr-2" /> Save Photo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ EDIT PROFILE MODAL ============ */}
      <Dialog open={showEditProfileModal} onOpenChange={(open) => {
        setShowEditProfileModal(open);
        if (open && user) {
          setEditProfileData({ 
            name: user.name || '', 
            first_name: user.first_name || '', 
            last_name: user.last_name || '', 
            email: user.email || '', 
            phone: user.phone || '',
            birth_month: user.birth_month || null,
            birth_day: user.birth_day || null,
          });
        }
      }}>
        <DialogContent className="max-w-xs p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Profile Photo */}
            <div className="flex items-center gap-3">
              <div 
                className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => profilePhotoRef.current?.click()}
              >
                {user?.profile_photo_url ? (
                  <img src={user.profile_photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <button
                onClick={() => profilePhotoRef.current?.click()}
                className="text-sm text-blue-600 hover:underline"
              >
                Change photo
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">First Name</label>
                <Input
                  value={editProfileData.first_name || ''}
                  onChange={(e) => {
                    const firstName = e.target.value;
                    const lastName = editProfileData.last_name || '';
                    setEditProfileData({
                      ...editProfileData, 
                      first_name: firstName,
                      name: `${firstName} ${lastName}`.trim()
                    });
                  }}
                  placeholder="First"
                  className="h-9"
                  data-testid="profile-first-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Last Name</label>
                <Input
                  value={editProfileData.last_name || ''}
                  onChange={(e) => {
                    const lastName = e.target.value;
                    const firstName = editProfileData.first_name || '';
                    setEditProfileData({
                      ...editProfileData, 
                      last_name: lastName,
                      name: `${firstName} ${lastName}`.trim()
                    });
                  }}
                  placeholder="Last"
                  className="h-9"
                  data-testid="profile-last-name"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Email</label>
              <Input
                type="email"
                value={editProfileData.email}
                onChange={(e) => setEditProfileData({...editProfileData, email: e.target.value})}
                placeholder="your@email.com"
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label>
              <div className="flex gap-2">
                <Input
                  value={editProfileData.phone}
                  className="h-9 flex-1 bg-gray-50"
                  disabled
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-9 text-xs"
                  onClick={() => {
                    setShowEditProfileModal(false);
                    toast.info("To change phone: Sign out → Sign in with new number → Contact support to merge accounts");
                  }}
                >
                  Change
                </Button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Phone is used for login</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Birthday <span className="text-gray-400 font-normal">(optional)</span></label>
              <div className="flex gap-2">
                <select
                  value={editProfileData.birth_month || ''}
                  onChange={(e) => setEditProfileData({...editProfileData, birth_month: e.target.value ? parseInt(e.target.value) : null})}
                  className="h-9 flex-1 border border-gray-200 rounded-md px-2 text-sm bg-white"
                  data-testid="profile-birth-month"
                >
                  <option value="">Month</option>
                  {[
                    "January","February","March","April","May","June",
                    "July","August","September","October","November","December"
                  ].map((m, i) => (
                    <option key={i+1} value={i+1}>{m}</option>
                  ))}
                </select>
                <select
                  value={editProfileData.birth_day || ''}
                  onChange={(e) => setEditProfileData({...editProfileData, birth_day: e.target.value ? parseInt(e.target.value) : null})}
                  className="h-9 w-24 border border-gray-200 rounded-md px-2 text-sm bg-white"
                  data-testid="profile-birth-day"
                >
                  <option value="">Day</option>
                  {Array.from({length: 31}, (_, i) => i+1).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Hosts may use this to celebrate birthdays.</p>
            </div>
          </div>
          <div className="flex gap-2 pt-3">
            <Button variant="outline" onClick={() => setShowEditProfileModal(false)} className="flex-1 h-9">Cancel</Button>
            <Button onClick={saveProfile} className="flex-1 h-9 bg-black hover:bg-gray-800">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ CATEGORY MANAGER MODAL ============ */}
      <Dialog open={showCategoryManager} onOpenChange={setShowCategoryManager}>
        <DialogContent className="max-w-xs p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              {visibleCategories.length === 0 ? "Build Your Card" : "Customize Card"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-600">
              {visibleCategories.length === 0 
                ? "Select the categories you use. Your card will only show what matters to you."
                : "Add or remove categories anytime. Only see what's relevant to you."
              }
            </p>
            
            {/* Card Color Section */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Card Color</p>
              <div className="flex gap-1.5">
                {BRAND_COLORS.map((color) => (
                  <button
                    key={color.id}
                    onClick={() => changeWalletColor(color.hex)}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${walletColor === color.hex ? 'border-gray-400 scale-110 ring-2 ring-offset-1 ring-gray-300' : 'border-gray-200 hover:scale-105'}`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">What services do you use?</p>
              <div className="space-y-2">
                {Object.entries(CATEGORIES).slice(0, 5).map(([catId, cat]) => {
                  const Icon = cat.icon;
                  const isVisible = visibleCategories.includes(catId);
                  
                  return (
                    <button
                      key={catId}
                      onClick={() => toggleCategory(catId)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                        isVisible 
                          ? 'border-green-200 bg-green-50' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl ${cat.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="flex-1 text-left font-medium">{cat.name}</span>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isVisible ? 'border-green-500 bg-green-500' : 'border-gray-300'
                      }`}>
                        {isVisible && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              You can always add categories back later
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Modal - Shows upcoming events and experiences with shareable links */}
      <Dialog open={showShareWalletModal} onOpenChange={(open) => { 
        setShowShareWalletModal(open); 
        if (!open) setSelectedShareExperience(null); 
      }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpFromLine className="w-5 h-5 text-pink-500" />
              Share Your Preference Card
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-4 space-y-4">
            {/* Show list of events and experiences with quick share */}
            {!selectedShareExperience ? (
              <div className="space-y-4">
                {/* Events Section */}
                {(() => {
                  // Deduplicate events using a Map
                  const eventMap = new Map();
                  hostedEvents.forEach(e => {
                    if (e.event_id && !eventMap.has(e.event_id)) {
                      eventMap.set(e.event_id, {...e, isHosting: true});
                    }
                  });
                  attendingEvents.forEach(e => {
                    if (e.event_id) {
                      if (eventMap.has(e.event_id)) {
                        eventMap.set(e.event_id, { ...eventMap.get(e.event_id), isAttending: true });
                      } else {
                        eventMap.set(e.event_id, { ...e, isAttending: true, isHosting: false });
                      }
                    }
                  });
                  const allEvents = Array.from(eventMap.values());
                  const upcomingEvents = allEvents.filter(e => e.date && new Date(e.date) >= new Date());
                  
                  if (upcomingEvents.length > 0) {
                    return (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Upcoming Events</p>
                        <div className="space-y-2">
                          {upcomingEvents.map((event) => (
                            <div 
                              key={event.event_id}
                              className="bg-gray-50 rounded-xl overflow-hidden"
                            >
                              <div className="p-3 space-y-2">
                              <div className="flex items-center gap-3">
                                {/* Square cover image */}
                                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                  {event.cover_image ? (
                                    <img 
                                      src={getImageUrl(event.cover_image)} 
                                      alt={event.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <PartyPopper className="w-6 h-6 text-white" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{event.name}</p>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <p className="text-xs text-gray-500">
                                      {new Date(event.date).toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})}
                                      {' • '}
                                      {new Date(event.date).toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}
                                      {' '}
                                      <span className="text-gray-400">{event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone.split('/')[1] || 'Local'}</span>
                                    </p>
                                    {event.isHosting && <Badge className="text-[9px] bg-violet-100 text-violet-700 px-1">Host</Badge>}
                                    {event.isAttending && <Badge className="text-[9px] bg-purple-100 text-purple-700 px-1">RSVP</Badge>}
                                  </div>
                                </div>
                              </div>
                              {/* Share buttons for this event */}
                              <div className="flex gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="h-8 px-2"
                                  onClick={() => {
                                    // Open the full preference card preview with edit capability
                                    setPreviewingEvent({...event, isAttending: event.isAttending});
                                    fetchEventRsvpPreferences(event.event_id);
                                    setShowEventPreferencePreview(true);
                                    setShowShareWalletModal(false);
                                  }}
                                  title="Preview & Edit preference card"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="flex-1 text-xs h-8"
                                  onClick={() => {
                                    const url = `${window.location.origin}/e/${event.slug || event.event_id}`;
                                    navigator.clipboard.writeText(url);
                                    toast.success("Event link copied!");
                                  }}
                                >
                                  <Copy className="w-3 h-3 mr-1" />
                                  Copy Link
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="flex-1 text-xs h-8"
                                  onClick={() => {
                                    const url = `${window.location.origin}/e/${event.slug || event.event_id}`;
                                    if (navigator.share) {
                                      navigator.share({ title: event.name, url: url });
                                    } else {
                                      window.open(`sms:?body=Check out ${event.name}: ${url}`, '_self');
                                    }
                                  }}
                                >
                                  <ArrowUpFromLine className="w-3 h-3 mr-1" />
                                  Share
                                </Button>
                              </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {/* Experiences Section */}
                {experiences.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Your Experiences</p>
                    <div className="space-y-2">
                      {experiences.map((exp) => {
                        const catConfig = CATEGORIES[exp.category] || CATEGORIES.nails;
                        const CatIcon = catConfig.icon;
                        return (
                          <div 
                            key={exp.experience_id}
                            className="bg-gray-50 rounded-xl p-3 space-y-2"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${catConfig.color} flex items-center justify-center flex-shrink-0`}>
                                <CatIcon className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{exp.name}</p>
                                <p className="text-xs text-gray-500">
                                  {exp.target_date ? new Date(exp.target_date).toLocaleDateString() : 'No date set'}
                                </p>
                              </div>
                            </div>
                            {/* Share buttons for this experience */}
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => openEditModal(exp)}
                                title="Preview preference card"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="flex-1 text-xs h-8"
                                onClick={() => {
                                  const url = `${window.location.origin}/share/${exp.share_token || exp.experience_id}`;
                                  navigator.clipboard.writeText(url);
                                  toast.success("Experience link copied!");
                                }}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy Link
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="flex-1 text-xs h-8"
                                onClick={() => setSelectedShareExperience(exp)}
                              >
                                <ChevronRight className="w-3 h-3 mr-1" />
                                More Options
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Empty state */}
                {(() => {
                  const hasEvents = hostedEvents.length > 0 || attendingEvents.length > 0;
                  if (!hasEvents && experiences.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No events or experiences yet</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-3"
                          onClick={() => { setShowShareWalletModal(false); startCreateExperience('hair'); }}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Create Experience
                        </Button>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ) : (
              <>
                {/* Experience Preview Card - This is what shows in iMessage/WhatsApp */}
                <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl p-4">
                  <p className="text-[10px] text-gray-400 mb-2">Link Preview</p>
                  <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    {/* Preview Header with inspo images */}
                    {(() => {
                      const linkedInspo = inspirations.filter(i => (selectedShareExperience.inspiration_ids || []).includes(i.inspiration_id));
                      if (linkedInspo.length > 0) {
                        return (
                          <div className="h-32 flex">
                            {linkedInspo.slice(0, 3).map((insp, idx) => (
                              <div key={idx} className="flex-1 h-full overflow-hidden">
                                <img 
                                  src={getDisplayImageUrl(insp.image_url)} 
                                  alt="" 
                                  className="w-full h-full object-cover" 
                                  onError={(e) => { e.target.src = PHYGITAL_AVATAR_BLACK; e.target.className = "w-full h-full object-contain p-4 bg-gray-100"; }}
                                />
                              </div>
                            ))}
                          </div>
                        );
                      }
                      const catConfig = CATEGORIES[selectedShareExperience.category] || CATEGORIES.nails;
                      return (
                        <div className={`h-32 ${catConfig.color} flex items-center justify-center`}>
                          <div className="text-center">
                            <PhygitalIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Preview Info */}
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        {user?.profile_photo_url && (
                          <img src={user.profile_photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                        )}
                        <span className="text-xs text-gray-500">{user?.name}'s Experience</span>
                      </div>
                      <h4 className="font-semibold text-sm">{selectedShareExperience.name}</h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedShareExperience.target_date 
                          ? new Date(selectedShareExperience.target_date).toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})
                          : 'Date TBD'}
                        {selectedShareExperience.appointment_time && ` at ${selectedShareExperience.appointment_time}`}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-2">phygital.systems</p>
                    </div>
                  </div>
                </div>
                
                {/* Share Options */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium">Share via:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      className="h-12 flex-col gap-1"
                      onClick={() => {
                        const url = `${window.location.origin}/share/${selectedShareExperience.share_token || selectedShareExperience.experience_id}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Link copied!");
                      }}
                    >
                      <Copy className="w-4 h-4" />
                      <span className="text-[10px]">Copy Link</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-12 flex-col gap-1"
                      onClick={() => {
                        const url = `${window.location.origin}/share/${selectedShareExperience.share_token || selectedShareExperience.experience_id}`;
                        window.open(`sms:?body=Check out my ${selectedShareExperience.name} experience: ${url}`, '_self');
                      }}
                    >
                      <Send className="w-4 h-4" />
                      <span className="text-[10px]">iMessage</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-12 flex-col gap-1"
                      onClick={() => {
                        const url = `${window.location.origin}/share/${selectedShareExperience.share_token || selectedShareExperience.experience_id}`;
                        window.open(`https://wa.me/?text=Check out my ${encodeURIComponent(selectedShareExperience.name)} experience: ${encodeURIComponent(url)}`, '_blank');
                      }}
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-[10px]">WhatsApp</span>
                    </Button>
                  </div>
                  
                  {/* NFC and Apple Wallet options */}
                  <div className="pt-2 border-t space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-3"
                      onClick={() => toast.info("NFC sharing coming soon!")}
                    >
                      <Smartphone className="w-4 h-4 text-blue-500" />
                      <span className="text-sm">Tap to Share (NFC)</span>
                      <Badge className="ml-auto text-[10px] bg-blue-100 text-blue-700">Soon</Badge>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-3"
                      onClick={() => toast.info("Apple Wallet integration coming soon!")}
                    >
                      <Download className="w-4 h-4 text-gray-600" />
                      <span className="text-sm">Add to Apple Wallet</span>
                      <Badge className="ml-auto text-[10px] bg-gray-100 text-gray-600">Soon</Badge>
                    </Button>
                  </div>
                </div>
                
                {/* Back button */}
                <Button 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => setSelectedShareExperience(null)}
                >
                  ← Back
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Wallet Preview Modal - Shows what shared link looks like */}
      <Dialog open={showWalletPreview} onOpenChange={setShowWalletPreview}>
        <DialogContent className="max-w-sm p-0 overflow-hidden rounded-2xl">
          <div className="p-4 bg-gray-100">
            <p className="text-xs text-gray-500 text-center mb-3">Preview: What others see when you share</p>
            {/* Mini Wallet Card Preview */}
            <div 
              className="rounded-2xl overflow-hidden text-white relative mx-auto max-w-[280px]"
              style={{ backgroundColor: walletColor }}
            >
              <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")'}} />
              <div className="p-4 relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                    {user?.profile_photo_url ? (
                      <img src={user.profile_photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <PhygitalIcon className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{user?.name || "Guest"}</h3>
                    <p className="text-white/70 text-xs">{user?.wallet_id || "PHY-XXXX"}</p>
                  </div>
                </div>
                {/* Category Icons */}
                {visibleCategories.filter(c => PILOT_CATEGORIES.includes(c)).length > 0 && (
                  <div className="flex gap-2">
                    {visibleCategories.filter(c => PILOT_CATEGORIES.includes(c)).map((catId) => {
                      const cat = CATEGORIES[catId];
                      if (!cat) return null;
                      const CatIcon = cat.icon;
                      return (
                        <div key={catId} className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                          <CatIcon className="w-3.5 h-3.5 text-white" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="p-4 space-y-2">
            <Button 
              onClick={() => { copyProfileLink(); }} 
              className="w-full bg-black hover:bg-gray-800"
            >
              <Copy className="w-4 h-4 mr-2" />
              {copied ? "Link Copied!" : "Copy Share Link"}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowWalletPreview(false)} 
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Account Modal */}
      <Dialog open={showDeleteAccountModal} onOpenChange={setShowDeleteAccountModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Account Forever
            </DialogTitle>
            <DialogDescription>
              This action is <strong>permanent and cannot be undone</strong>. All your data will be deleted including:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Your profile and preferences</li>
              <li>All inspiration photos</li>
              <li>All experiences and appointments</li>
              <li>Event RSVPs and history</li>
              <li>Linked accounts and sessions</li>
            </ul>
            
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                Type <strong>DELETE</strong> to confirm permanent deletion
              </p>
            </div>
            
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="border-red-200 focus:ring-red-500"
              data-testid="delete-confirmation-input"
            />
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Reason for leaving (optional)</label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Help us improve..."
                className="w-full p-2 border border-gray-200 rounded-lg text-sm resize-none"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowDeleteAccountModal(false);
              setDeleteConfirmation("");
              setDeleteReason("");
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deletingAccount || deleteConfirmation !== "DELETE"}
              data-testid="confirm-delete-btn"
            >
              {deletingAccount ? "Deleting..." : "Delete Forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* REMOVED: showEventPreferencePreview Dialog - was displaying wrong date and redundant popup */}
      
      {/* Edit RSVP Modal - Can't Make It */}
      <Dialog open={showEditRsvpModal} onOpenChange={setShowEditRsvpModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update RSVP</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you can't make it to <span className="font-medium">{previewingEvent?.name}</span>?
            </p>
            
            {previewingEvent?.isHosting && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                <p className="text-xs text-violet-700">
                  <strong>Note:</strong> You'll remain as a Host for this event. Only your Attending status will be removed.
                </p>
              </div>
            )}
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700">
                {previewingEvent?.isHosting 
                  ? "Your 'Attending' badge will be removed, but the event will stay in Upcoming since you're the Host."
                  : "The event will be removed from your Upcoming section. You can always sign up again later."
                }
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => setShowEditRsvpModal(false)}
              >
                Keep RSVP
              </Button>
              <Button 
                variant="destructive"
                className="flex-1"
                onClick={async () => {
                  if (editingRsvp?.rsvp_id && previewingEvent?.event_id) {
                    const success = await updateRsvpStatus(previewingEvent.event_id, editingRsvp.rsvp_id, 'cancelled');
                    if (success) {
                      toast.success("RSVP cancelled. " + (previewingEvent?.isHosting ? "You're still the Host." : ""));
                    }
                    setShowEventPreferencePreview(false);
                  }
                }}
              >
                Can't Make It
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Experience Preview Modal - Preview and edit experiences with dates */}
      <Dialog open={showExperiencePreview} onOpenChange={setShowExperiencePreview}>
        <DialogContent className="max-w-sm p-0 overflow-hidden">
          {previewingExperience && (() => {
            const catConfig = CATEGORIES[previewingExperience.category] || CATEGORIES.nails;
            const CatIcon = catConfig.icon;
            const coverImg = getCoverImage(previewingExperience);
            
            return (
              <div className="relative">
                {/* Category-colored header */}
                <div 
                  className="text-white p-4"
                  style={{ 
                    background: `linear-gradient(135deg, ${catConfig.gradient.includes('blue') ? '#2563eb, #0d9488' : 
                      catConfig.gradient.includes('pink') ? '#db2777, #9333ea' :
                      catConfig.gradient.includes('purple') ? '#a855f7, #6366f1' :
                      catConfig.gradient.includes('amber') ? '#f59e0b, #ea580c' :
                      '#000, #333'})`
                  }}
                >
                  {/* Experience header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/20 shadow-sm flex-shrink-0">
                      {coverImg ? (
                        <img 
                          src={coverImg} 
                          alt={previewingExperience.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <CatIcon className="w-6 h-6 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Badge className="bg-white/20 text-white text-[9px] mb-1">{catConfig.name.toUpperCase()}</Badge>
                      <h3 className="font-bold text-sm truncate">{previewingExperience.name}</h3>
                      {previewingExperience.target_date && (
                        <p className="text-xs text-white/70">
                          {new Date(previewingExperience.target_date).toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Experience details */}
                  <div className="bg-white/10 rounded-xl p-3 space-y-2">
                    {/* Provider info */}
                    {previewingExperience.provider_info?.name ? (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-white/70" />
                        <span className="text-sm">with {previewingExperience.provider_info.name}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-white/70" />
                        <span className="text-sm">DIY / Self-service</span>
                      </div>
                    )}
                    
                    {/* Stylist note */}
                    {previewingExperience.stylist_note && (
                      <div className="text-xs">
                        <span className="text-white/60">Note: </span>
                        <span>{previewingExperience.stylist_note}</span>
                      </div>
                    )}
                    
                    {/* Inspiration count */}
                    {previewingExperience.inspiration_images?.length > 0 && (
                      <div className="text-xs">
                        <span className="text-white/60">Inspiration: </span>
                        <span>{previewingExperience.inspiration_images.length} photos attached</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-[10px] text-white/50 text-center mt-3">
                    This is what providers see when you share
                  </p>
                </div>
                
                {/* Actions */}
                <div className="p-4 bg-white">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setShowExperiencePreview(false);
                        openEditModal(previewingExperience);
                      }}
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      Edit Experience
                    </Button>
                    <Button 
                      className="flex-1 bg-black hover:bg-gray-800"
                      onClick={() => {
                        shareExperience(previewingExperience.experience_id);
                        setShowExperiencePreview(false);
                      }}
                    >
                      <ArrowUpFromLine className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
      
      {/* Edit Event Defaults Modal - Matches event invite styling exactly */}
      <Dialog open={showEditEventDefaults} onOpenChange={setShowEditEventDefaults}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Accessibility className="w-5 h-5 text-teal-600" />
              Accessibility
            </DialogTitle>
            <DialogDescription>
              How can we accommodate you?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-2 overflow-y-auto flex-1">
            {/* Physical accommodations - EXACT styling from RSVP intake */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-700">Physical accommodations</p>
              <div className="grid grid-cols-2 gap-2">
                {ACCESSIBILITY_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const currentNeeds = preferences?.accessibility?.accessibility_needs || [];
                  const isSelected = Array.isArray(currentNeeds) && currentNeeds.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => {
                        const current = Array.isArray(currentNeeds) ? currentNeeds : [];
                        const updated = current.includes(option.id) 
                          ? current.filter(n => n !== option.id)
                          : [...current, option.id];
                        setPreferences({
                          ...preferences, 
                          accessibility: { ...preferences.accessibility, accessibility_needs: updated }
                        });
                      }}
                      className={`p-2.5 rounded-xl border text-left transition-all relative ${
                        isSelected
                          ? "border-gray-900 bg-gray-50 shadow-sm"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-8 h-8 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-xs text-gray-900 leading-tight">{option.label}</p>
                          <p className="text-xs text-gray-600 mt-0.5 leading-tight">{option.desc}</p>
                        </div>
                      </div>
                      {isSelected && <CheckCircle2 className="w-4 h-4 text-green-500 absolute top-2 right-2" />}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Invisible Disabilities - EXACT styling from RSVP intake */}
            <div className="space-y-3">
              <button
                onClick={() => setExpandedAccessibility(prev => ({ ...prev, invisible: !prev.invisible }))}
                className="flex items-center gap-2 w-full text-left"
              >
                <div className="w-5 h-5 rounded-full bg-pink-100 flex items-center justify-center">
                  <Heart className="w-2.5 h-2.5 text-pink-500" fill="currentColor" />
                </div>
                <span className="text-xs font-medium text-gray-700">Invisible Disabilities</span>
                <span className="text-[10px] text-gray-500">(optional)</span>
                <ChevronRight className={`w-4 h-4 text-gray-500 ml-auto transition-transform ${expandedAccessibility.invisible ? 'rotate-90' : ''}`} />
              </button>
              
              {expandedAccessibility.invisible && (
                <div className="space-y-2 animate-fade-in pl-2">
                  <p className="text-[10px] text-gray-600 mb-2">
                    Share what helps us support you best. This info stays private with event hosts.
                  </p>
                  {INVISIBLE_DISABILITIES.map((option) => {
                    const Icon = option.icon;
                    const currentInvisible = preferences?.accessibility?.invisible_disabilities || [];
                    const isSelected = Array.isArray(currentInvisible) && currentInvisible.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => {
                          const current = Array.isArray(currentInvisible) ? currentInvisible : [];
                          const updated = current.includes(option.id) 
                            ? current.filter(n => n !== option.id)
                            : [...current, option.id];
                          setPreferences({
                            ...preferences, 
                            accessibility: { ...preferences.accessibility, invisible_disabilities: updated }
                          });
                        }}
                        className={`w-full p-2.5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? "border-gray-900 bg-gray-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-8 h-8 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-xs text-gray-900">{option.label}</p>
                              {isSelected && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                            </div>
                            <p className="text-[10px] text-gray-500 mt-0.5">{option.desc}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
              
            {/* Additional notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Anything else we should know?</label>
              <textarea
                placeholder="Share any additional accessibility needs..."
                className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none bg-white text-gray-900"
                rows={3}
                value={preferences?.accessibility?.additional_notes || ''}
                onChange={(e) => setPreferences({
                  ...preferences, 
                  accessibility: { ...preferences.accessibility, additional_notes: e.target.value }
                })}
              />
            </div>
            
            {/* Dietary Section */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Utensils className="w-5 h-5 text-orange-600" />
                <h4 className="font-medium text-sm text-gray-800">Dietary Restrictions</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'vegan', label: 'Vegan' },
                  { id: 'vegetarian', label: 'Vegetarian' },
                  { id: 'gluten_free', label: 'Gluten-free' },
                  { id: 'dairy_free', label: 'Dairy-free' },
                  { id: 'nut_free', label: 'Nut-free' },
                  { id: 'kosher', label: 'Kosher' },
                  { id: 'halal', label: 'Halal' },
                  { id: 'pescatarian', label: 'Pescatarian' }
                ].map((option) => {
                  const currentDietary = preferences?.dietary?.dietary_restrictions || [];
                  const isSelected = Array.isArray(currentDietary) && currentDietary.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => {
                        const current = Array.isArray(currentDietary) ? currentDietary : [];
                        const updated = current.includes(option.id) 
                          ? current.filter(n => n !== option.id)
                          : [...current, option.id];
                        setPreferences({
                          ...preferences, 
                          dietary: { ...preferences.dietary, dietary_restrictions: updated }
                        });
                      }}
                      className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                        isSelected
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              
              {/* Allergies */}
              <div className="mt-2">
                <label className="text-xs text-gray-600 mb-1 block">Allergies</label>
                <input
                  type="text"
                  value={(preferences?.dietary?.food_allergies || []).join(', ')}
                  onChange={(e) => setPreferences({
                    ...preferences, 
                    dietary: { ...preferences.dietary, food_allergies: e.target.value.split(',').map(a => a.trim()).filter(Boolean) }
                  })}
                  className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-900"
                  placeholder="e.g., peanuts, shellfish"
                />
              </div>
            </div>
            
            {/* Seating Preference */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                <h4 className="font-medium text-sm text-gray-800">Seating Preference</h4>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['Front', 'Middle', 'Back', 'Aisle', 'Window', 'No Preference'].map((pref) => (
                  <button
                    key={pref}
                    onClick={() => setPreferences({
                      ...preferences, 
                      events: { ...preferences.events, seating_preference: pref }
                    })}
                    className={`p-2 rounded-lg border text-xs transition-colors ${
                      preferences?.events?.seating_preference === pref
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    {pref}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Legal Disclaimer */}
            <div className="pt-4 border-t">
              <p className="text-[10px] text-gray-400 leading-relaxed">
                <strong>Disclaimer:</strong> Phygital is a communication platform that facilitates sharing of preferences between attendees and event hosts/venues. Phygital is not responsible for any health, accessibility, or dietary accommodations. All responsibility for providing appropriate accommodations lies with the event host and venue. By sharing your preferences, you acknowledge that final accommodation decisions are made by the parties involved.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditEventDefaults(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={async () => {
                // Save each category to backend
                try {
                  const token = localStorage.getItem("session_token");
                  
                  // Save accessibility preferences
                  if (preferences.accessibility) {
                    await fetch(`${API}/user/preferences/accessibility`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify(preferences.accessibility)
                    });
                  }
                  
                  // Save dietary preferences
                  if (preferences.dietary) {
                    await fetch(`${API}/user/preferences/dietary`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify(preferences.dietary)
                    });
                  }
                  
                  // Save events preferences
                  if (preferences.events) {
                    await fetch(`${API}/user/preferences/events`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify(preferences.events)
                    });
                  }
                  
                  toast.success("Preferences saved!");
                  setShowEditEventDefaults(false);
                } catch (err) {
                  toast.error("Failed to save preferences");
                }
              }}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Events Default Modal - Seating & Vibe Preferences */}
      <Dialog open={showEventsDefaultModal} onOpenChange={setShowEventsDefaultModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PartyPopper className="w-5 h-5 text-purple-600" />
              Event Preferences
            </DialogTitle>
            <DialogDescription>
              Your default preferences for events
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2 overflow-y-auto flex-1">
            {/* Seating Preference */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-800">Seating Preference</h4>
              <div className="grid grid-cols-3 gap-2">
                {['Front', 'Middle', 'Back', 'Aisle', 'Window', 'No Preference'].map((pref) => (
                  <button
                    key={pref}
                    onClick={() => setPreferences({
                      ...preferences, 
                      events: { ...preferences.events, seating_preference: pref }
                    })}
                    className={`p-3 rounded-xl border text-sm transition-colors ${
                      preferences?.events?.seating_preference === pref
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    {pref}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Additional event notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Additional notes</label>
              <textarea
                placeholder="Anything else about your event preferences..."
                className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none bg-white text-gray-900"
                rows={3}
                value={preferences?.events?.additional_notes || ''}
                onChange={(e) => setPreferences({
                  ...preferences, 
                  events: { ...preferences.events, additional_notes: e.target.value }
                })}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEventsDefaultModal(false)}>Cancel</Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("session_token");
                  await fetch(`${API}/user/preferences/events`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify(preferences.events)
                  });
                  toast.success("Event preferences saved!");
                  setShowEventsDefaultModal(false);
                } catch (err) {
                  toast.error("Failed to save preferences");
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Accessibility Default Modal */}
      <Dialog open={showAccessibilityDefaultModal} onOpenChange={setShowAccessibilityDefaultModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Accessibility className="w-5 h-5 text-teal-600" />
              Accessibility Defaults
            </DialogTitle>
            <DialogDescription>
              Set your default accessibility preferences. These will pre-fill when you RSVP to events.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-2 overflow-y-auto flex-1">
            {/* Physical accommodations - EXACT styling from RSVP intake */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-700">Physical accommodations</p>
              <div className="grid grid-cols-2 gap-2">
                {ACCESSIBILITY_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const currentNeeds = preferences?.accessibility?.accessibility_needs || [];
                  const isSelected = Array.isArray(currentNeeds) && currentNeeds.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => {
                        const current = Array.isArray(currentNeeds) ? currentNeeds : [];
                        const updated = current.includes(option.id) 
                          ? current.filter(n => n !== option.id)
                          : [...current, option.id];
                        setPreferences({
                          ...preferences, 
                          accessibility: { ...preferences.accessibility, accessibility_needs: updated }
                        });
                      }}
                      className={`p-2.5 rounded-xl border text-left transition-all relative ${
                        isSelected
                          ? "border-gray-900 bg-gray-50 shadow-sm"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-8 h-8 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-xs text-gray-900 leading-tight">{option.label}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{option.desc}</p>
                        </div>
                      </div>
                      {isSelected && <CheckCircle2 className="w-4 h-4 text-green-500 absolute top-2 right-2" />}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Invisible Disabilities - collapsible like RSVP */}
            <div className="space-y-3">
              <button
                onClick={() => setExpandedAccessibility(prev => ({ ...prev, invisibleDefaults: !prev.invisibleDefaults }))}
                className="flex items-center gap-2 w-full text-left"
              >
                <div className="w-5 h-5 rounded-full bg-pink-100 flex items-center justify-center">
                  <Heart className="w-2.5 h-2.5 text-pink-500" fill="currentColor" />
                </div>
                <span className="text-xs font-medium text-gray-700">Invisible Disabilities</span>
                <span className="text-[10px] text-gray-500">(optional)</span>
                <ChevronRight className={`w-4 h-4 text-gray-500 ml-auto transition-transform ${expandedAccessibility?.invisibleDefaults ? 'rotate-90' : ''}`} />
              </button>
              
              {expandedAccessibility?.invisibleDefaults && (
                <div className="space-y-2 animate-fade-in pl-2">
                  <p className="text-[10px] text-gray-500 mb-2">
                    Share what helps us support you best. This info stays private with event hosts.
                  </p>
                  {INVISIBLE_DISABILITIES.map((option) => {
                    const Icon = option.icon;
                    const currentInvisible = preferences?.accessibility?.invisible_disabilities || [];
                    const isSelected = Array.isArray(currentInvisible) && currentInvisible.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => {
                          const current = Array.isArray(currentInvisible) ? currentInvisible : [];
                          const updated = current.includes(option.id) 
                            ? current.filter(n => n !== option.id)
                            : [...current, option.id];
                          setPreferences({
                            ...preferences, 
                            accessibility: { ...preferences.accessibility, invisible_disabilities: updated }
                          });
                        }}
                        className={`w-full p-2.5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? "border-gray-900 bg-gray-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-8 h-8 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-xs text-gray-900">{option.label}</p>
                              {isSelected && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                            </div>
                            <p className="text-[10px] text-gray-500 mt-0.5">{option.desc}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Additional notes */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700">Anything else to share?</p>
              <textarea
                className="w-full text-sm p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-gray-400"
                rows={2}
                placeholder="Add any other accessibility needs or preferences..."
                value={preferences?.accessibility?.additional_notes || ''}
                onChange={(e) => setPreferences({
                  ...preferences, 
                  accessibility: { ...preferences.accessibility, additional_notes: e.target.value }
                })}
              />
            </div>
          </div>
          
          <div className="pt-4 border-t flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowAccessibilityDefaultModal(false)}
            >
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-gray-900 hover:bg-gray-800"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("session_token");
                  await fetch(`${API}/preferences/accessibility`, {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(preferences?.accessibility || {})
                  });
                  toast.success("Accessibility preferences saved!");
                  setShowAccessibilityDefaultModal(false);
                  revalidateAllUserData();
                } catch (err) {
                  toast.error("Failed to save accessibility defaults");
                }
              }}
            >
              Save Defaults
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dietary Default Modal */}
      <Dialog open={showDietaryDefaultModal} onOpenChange={setShowDietaryDefaultModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Utensils className="w-5 h-5 text-orange-600" />
              Dietary Preferences
            </DialogTitle>
            <DialogDescription>
              What are your dietary needs?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-2 overflow-y-auto flex-1">
            {/* Dietary Restrictions */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-800">Dietary Restrictions</h4>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'vegan', label: 'Vegan' },
                  { id: 'vegetarian', label: 'Vegetarian' },
                  { id: 'gluten_free', label: 'Gluten-free' },
                  { id: 'dairy_free', label: 'Dairy-free' },
                  { id: 'nut_free', label: 'Nut-free' },
                  { id: 'kosher', label: 'Kosher' },
                  { id: 'halal', label: 'Halal' },
                  { id: 'pescatarian', label: 'Pescatarian' }
                ].map((option) => {
                  const currentDietary = preferences?.dietary?.dietary_restrictions || [];
                  const isSelected = Array.isArray(currentDietary) && currentDietary.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => {
                        const current = Array.isArray(currentDietary) ? currentDietary : [];
                        const updated = current.includes(option.id) 
                          ? current.filter(n => n !== option.id)
                          : [...current, option.id];
                        setPreferences({
                          ...preferences, 
                          dietary: { ...preferences.dietary, dietary_restrictions: updated }
                        });
                      }}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        isSelected
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Allergies */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Allergies</label>
              <input
                type="text"
                value={(preferences?.dietary?.food_allergies || []).join(', ')}
                onChange={(e) => setPreferences({
                  ...preferences, 
                  dietary: { ...preferences.dietary, food_allergies: e.target.value.split(',').map(a => a.trim()).filter(Boolean) }
                })}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white text-gray-900"
                placeholder="e.g., peanuts, shellfish, soy"
              />
            </div>
            
            {/* Additional notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Additional dietary notes</label>
              <textarea
                placeholder="Anything else about your dietary needs..."
                className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none bg-white text-gray-900"
                rows={3}
                value={preferences?.dietary?.additional_notes || ''}
                onChange={(e) => setPreferences({
                  ...preferences, 
                  dietary: { ...preferences.dietary, additional_notes: e.target.value }
                })}
              />
            </div>
            
            {/* Legal Disclaimer */}
            <p className="text-[10px] text-gray-400 leading-relaxed pt-2 border-t">
              <strong>Disclaimer:</strong> Phygital is a communication platform. We are not responsible for any dietary accommodations. All responsibility lies with the event host/venue.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDietaryDefaultModal(false)}>Cancel</Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("session_token");
                  await fetch(`${API}/user/preferences/dietary`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify(preferences.dietary)
                  });
                  toast.success("Dietary preferences saved!");
                  setShowDietaryDefaultModal(false);
                } catch (err) {
                  toast.error("Failed to save preferences");
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Interests Default Modal */}
      <Dialog open={showInterestsDefaultModal} onOpenChange={setShowInterestsDefaultModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-600" />
              Your Interests
            </DialogTitle>
            <DialogDescription>
              What are you passionate about? These help you connect with like-minded people.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-2 overflow-y-auto flex-1">
            {/* Interest Categories */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-800">Select Your Interests</h4>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'networking', label: 'Networking' },
                  { id: 'fashion', label: 'Fashion' },
                  { id: 'tech', label: 'Tech' },
                  { id: 'ai_ml', label: 'AI & Machine Learning' },
                  { id: 'sustainability', label: 'Sustainability' },
                  { id: 'art_design', label: 'Art & Design' },
                  { id: 'web3', label: 'Web3' },
                  { id: 'investing', label: 'Investing' },
                  { id: 'startups', label: 'Startups' },
                  { id: 'marketing', label: 'Marketing' },
                  { id: 'music', label: 'Music' },
                  { id: 'film', label: 'Film & Video' },
                  { id: 'wellness', label: 'Wellness' },
                  { id: 'fitness', label: 'Fitness' },
                  { id: 'travel', label: 'Travel' },
                  { id: 'food_wine', label: 'Food & Wine' },
                  { id: 'gaming', label: 'Gaming' },
                  { id: 'sports', label: 'Sports' },
                  { id: 'real_estate', label: 'Real Estate' },
                  { id: 'finance', label: 'Finance' },
                  { id: 'education', label: 'Education' },
                  { id: 'nonprofit', label: 'Nonprofit' }
                ].map((option) => {
                  const currentInterests = preferences?.interests?.categories || [];
                  const isSelected = Array.isArray(currentInterests) && currentInterests.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => {
                        const current = Array.isArray(currentInterests) ? currentInterests : [];
                        const updated = current.includes(option.id) 
                          ? current.filter(n => n !== option.id)
                          : [...current, option.id];
                        setPreferences({
                          ...preferences, 
                          interests: { ...preferences?.interests, categories: updated }
                        });
                      }}
                      className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                        isSelected
                          ? 'bg-rose-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Custom Interests */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Other Interests</label>
              <input
                type="text"
                value={(preferences?.interests?.custom || []).join(', ')}
                onChange={(e) => setPreferences({
                  ...preferences, 
                  interests: { ...preferences?.interests, custom: e.target.value.split(',').map(a => a.trim()).filter(Boolean) }
                })}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white text-gray-900"
                placeholder="Add custom interests, comma-separated"
              />
            </div>
            
            {/* Music Preferences (part of expanded Interests) */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Favorite Music Genres</label>
              <div className="flex flex-wrap gap-2">
                {['Hip Hop', 'R&B', 'Pop', 'Electronic', 'Rock', 'Jazz', 'Classical', 'Country', 'Latin', 'Afrobeats'].map((genre) => {
                  const currentGenres = preferences?.interests?.music_genres || [];
                  const isSelected = currentGenres.includes(genre);
                  return (
                    <button
                      key={genre}
                      onClick={() => {
                        const updated = isSelected 
                          ? currentGenres.filter(g => g !== genre)
                          : [...currentGenres, genre];
                        setPreferences({
                          ...preferences, 
                          interests: { ...preferences?.interests, music_genres: updated }
                        });
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        isSelected
                          ? 'bg-violet-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {genre}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowInterestsDefaultModal(false)}>Cancel</Button>
            <Button 
              className="bg-rose-600 hover:bg-rose-700"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("session_token");
                  await fetch(`${API}/user/preferences/interests`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify(preferences?.interests || {})
                  });
                  toast.success("Interests saved!");
                  setShowInterestsDefaultModal(false);
                } catch (err) {
                  toast.error("Failed to save interests");
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Event/Experience Preview Modal - Unified Preference Card (same as host view) */}
      <UnifiedPreferenceCard
        person={{
          name: user?.name,
          email: user?.email,
          phone: user?.phone,
          profile_photo: user?.profile_photo,
          profile_photo_url: user?.profile_photo || user?.profile_picture || user?.profile_image,
          profile_image: user?.profile_photo || user?.profile_picture || user?.profile_image,
          picture: user?.profile_photo || user?.profile_picture || user?.profile_image,
          company: user?.company,
          role: user?.role,
          is_vip: user?.is_vip,
          tags: user?.tags,
          // Map RSVP preferences
          dietary_restrictions: previewEvent?.rsvpPreferences?.dietary?.dietary_restrictions || [],
          accessibility_needs: previewEvent?.rsvpPreferences?.accessibility?.accessibility_needs || [],
          invisible_disabilities: previewEvent?.rsvpPreferences?.accessibility?.invisible_disabilities || [],
          interests: previewEvent?.rsvpPreferences?.interests || [],
          notes: previewEvent?.rsvpPreferences?.accessibility?.notes,
          vibe: previewEvent?.rsvpPreferences?.vibe
        }}
        event={previewEvent}
        open={!!previewEvent}
        onOpenChange={(open) => !open && setPreviewEvent(null)}
        onEdit={() => {
          if (previewEvent?.event_id) {
            const slug = previewEvent.slug || previewEvent.event_id;
            navigate(`/e/${slug}/confirmation?view=preferences`);
          }
          setPreviewEvent(null);
        }}
        eventId={previewEvent?.event_id}
        variant="modal"
      />
      
      {/* Share Preference Card Modal with Visual Preview */}
      <SharePreferenceCardModal
        open={showSharePreferenceCard}
        onOpenChange={setShowSharePreferenceCard}
        user={user}
        userDefaults={userDefaults?.defaults}
        cardColor={walletColor}
        onViewPreferences={() => setMobileTab("settings")}
        experiences={experiences}
      />

      {/* Plan a Service modal - opens as overlay on /me dashboard. Saves to user_planned_experiences (visible in Experiences tab afterwards). User stays on Dashboard, then auto-redirects to Share Card page (screen 14 in the Phygital flow). */}
      <PlanExperienceModal
        isOpen={showPlanService}
        onClose={() => { setShowPlanService(false); setPlanServicePrefillCategory(null); }}
        prefilledCategoryId={planServicePrefillCategory}
        onSelect={async (payload) => {
          try {
            const token = localStorage.getItem("session_token");
            const r = await fetch(`${API}/user/experiences`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify(payload)
            });
            if (!r.ok) throw new Error("save_failed");
            const savedExp = await r.json().catch(() => ({}));
            const savedExpId = savedExp?.experience_id || savedExp?.experience?.experience_id;
            toast.success("Experience saved");
            setShowPlanService(false);
            // Auto-generate a share_id and redirect to the Share Card page.
            if (savedExpId) {
              try {
                const shareResp = await fetch(`${API}/user/experiences/${savedExpId}/share`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (shareResp.ok) {
                  const { share_id } = await shareResp.json();
                  if (share_id) { navigate(`/share/card/${share_id}`); return; }
                }
              } catch { /* fall through to Experiences tab */ }
            }
            setMobileTab("experiences");
          } catch (err) {
            toast.error("Could not save experience. Please try again.");
          }
        }}
      />

      {/* Plan around an Occasion modal - POSTs to /user/experiences/plan-event which auto-creates linked experiences */}
      <PlanEventModal
        isOpen={showPlanOccasion}
        onClose={() => setShowPlanOccasion(false)}
        onSelect={async (payload) => {
          try {
            const token = localStorage.getItem("session_token");
            // Map services array (category ids) to suggested_services
            const r = await fetch(`${API}/user/experiences/plan-event`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                event_type: payload.event_type,
                name: payload.name,
                date: payload.date,
                suggested_services: payload.services || [],
                service_providers: payload.service_providers || {},
                service_dates: payload.service_dates || {},
                service_provider_details: payload.service_provider_details || {}
              })
            });
            if (!r.ok) throw new Error("save_failed");
            const data = await r.json();
            toast.success(`Saved occasion plus ${data.total_created - 1} linked services`);
            setShowPlanOccasion(false);
            setMobileTab("experiences");
          } catch (err) {
            toast.error("Could not save occasion. Please try again.");
          }
        }}
      />
      
      {/* Preference Card Modal (triggered by eyeball icon) */}
      <PreferenceCardModal
        open={showPreferenceCardModal}
        onClose={setShowPreferenceCardModal}
        user={{
          ...user,
          defaults: userDefaults?.defaults
        }}
        category="default"
        roleTags={user?.tags?.filter(t => t.startsWith('role_') || t.startsWith('pilot_')).map(t => 
          t.replace('role_', '').replace('pilot_', '').replace(/_/g, ' ').charAt(0).toUpperCase() + 
          t.replace('role_', '').replace('pilot_', '').replace(/_/g, ' ').slice(1)
        ) || []}
      />
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Are you sure?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              Delete folder "{deleteConfirm.folder}"? This will remove the folder and all images inside it.
            </p>
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">Cancel</Button>
              <Button
                variant="destructive"
                data-testid="confirm-delete-btn"
                onClick={async () => {
                  const cat = deleteConfirm.category;
                  const folder = deleteConfirm.folder;
                  const updated = { ...inspoSubfolders };
                  updated[cat] = (updated[cat] || []).filter(f => f !== folder);
                  setInspoSubfolders(updated);
                  if (activeSubfolder === folder) setActiveSubfolder(null);
                  const token = localStorage.getItem("session_token");
                  await fetch(`${API}/me/inspo-subfolders`, {
                    method: "PUT",
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                    body: JSON.stringify({ folders: updated })
                  });
                  setDeleteConfirm(null);
                  toast.success("Folder deleted");
                }}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Iter 9.40 Pass F - Host Notes modal (Pre/During/Post). Stage is
          derived from days-until-event:
            • > 0 days remaining  → "pre"
            • day-of (within 24h) → "during"
            • past                → "post"
      */}
      {hostNoteEvent && (() => {
        const eventDateStr = hostNoteEvent.date || hostNoteEvent.appointment_date;
        let derivedStage = "pre";
        if (eventDateStr) {
          try {
            const eventDate = new Date(eventDateStr);
            const now = new Date();
            const diffMs = eventDate.getTime() - now.getTime();
            const oneDayMs = 24 * 60 * 60 * 1000;
            if (diffMs <= -oneDayMs)    derivedStage = "post";
            else if (diffMs <= oneDayMs) derivedStage = "during";
            else                         derivedStage = "pre";
          } catch { /* fallback to pre */ }
        }
        return (
          <HostNoteModal
            open={!!hostNoteEvent}
            onClose={() => setHostNoteEvent(null)}
            hostUserId={hostNoteEvent.host_user_id}
            stage={derivedStage}
            categoryId={hostNoteEvent.pilot_category}
            clientPreferences={userDefaults?.defaults || {}}
          />
        );
      })()}
      <AdminBar />
    </div>
  );
};

export default UserProfilePage;
